import React, { useState, useEffect, useCallback, useRef } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { formatDateDMY } from '../utils/dateFormat';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { invoiceAPI, customerAPI, productAPI, settingsAPI, authAPI, paymentAPI, supplierAPI, bulkAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { useData } from '../contexts/DataContext';
import { QRCodeSVG } from 'qrcode.react';
import logo from '../logo.png';
import LiveOCRScanner from '../components/LiveOCRScanner';
import { resolveMediaUrl } from '../utils/mediaUrl';

/* ── Constants ─────────────────────────────── */
const BILL_TYPES = [
  { value: 'tax_invoice',    label: 'Tax Invoice',      desc: 'GST B2B / B2C',         icon: '🧾' },
  { value: 'non_gst',        label: 'Non-GST Bill',     desc: 'Simple cash memo',       icon: '🧾' },
  { value: 'bill_of_supply', label: 'Bill of Supply',   desc: 'Composition / exempt',   icon: '📄' },
];
const BILL_LABELS = {
  tax_invoice: 'TAX INVOICE', non_gst: 'BILL / RECEIPT',
  quotation: 'QUOTATION', bill_of_supply: 'BILL OF SUPPLY', proforma: 'PROFORMA INVOICE',
};
const UNITS = ['Pcs', 'Nos', 'Kg', 'Gm', 'Ltr', 'Ml', 'Mtr', 'Cm', 'Ft', 'Sqft', 'Box', 'Pack', 'Set', 'Pair', 'Roll', 'Sheet', 'Bag', 'Can', 'Btl', 'Doz'];
const GST_RATES = [0, 3, 5, 12, 18, 28];
const GST_OPTIONS = GST_RATES.flatMap(r => r === 0
  ? [{ value: '0-ex', label: '0%' }]
  : [{ value: `${r}-in`, label: `${r}%-In` }, { value: `${r}-ex`, label: `${r}%-Ex` }]
);
const gstSelectVal = (item) => {
  const pct = n(item.gst_percent);
  if (pct === 0) return '0-ex';
  return `${pct}-${item.gst_inclusive ? 'in' : 'ex'}`;
};
const PAYMENT_MODES = ['Cash', 'UPI', 'Card', 'Bank Transfer', 'Cheque', 'Finance', 'Credit', 'Online'];

const fmtDate = d => formatDateDMY(d, '');
const fmtDMY = d => formatDateDMY(d, '');
const fmtNum = n => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const n = v => parseFloat(v) || 0;
const billToAddress = inv => inv.bill_to_address || inv.party_address || inv.customer_address || '';
const deliveryAddressOf = inv => inv.delivery_address || inv.customer_address || '';
const billToPhone = inv => inv.bill_to_phone || inv.customer_phone || '';

const numToWords = (num) => {
  if (!num || num === 0) return 'Zero';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const conv = (x) => {
    if (x < 20) return ones[x];
    if (x < 100) return tens[~~(x/10)] + (x%10 ? ' '+ones[x%10] : '');
    if (x < 1000) return ones[~~(x/100)] + ' Hundred' + (x%100 ? ' '+conv(x%100) : '');
    if (x < 100000) return conv(~~(x/1000)) + ' Thousand' + (x%1000 ? ' '+conv(x%1000) : '');
    if (x < 10000000) return conv(~~(x/100000)) + ' Lakh' + (x%100000 ? ' '+conv(x%100000) : '');
    return conv(~~(x/10000000)) + ' Crore' + (x%10000000 ? ' '+conv(x%10000000) : '');
  };
  return conv(Math.round(num));
};

/* ── UPI QR Code Component ─────────────────── */
const UpiQrCode = ({ upiId, shopName, amount, size = 100 }) => {
  if (!upiId) return null;
  const amt = (Math.round(parseFloat(amount) || 0)).toFixed(2);
  const upiUrl = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(shopName || '')}&am=${amt}&cu=INR`;
  return (
    <div style={{ textAlign: 'center' }}>
      <QRCodeSVG value={upiUrl} size={size} level="M" />
      <div style={{ fontSize: 9, color: '#374151', marginTop: 4, fontWeight: 600 }}>Scan to Pay ₹{amt}</div>
      <div style={{ fontSize: 8, color: '#6b7280' }}>{upiId}</div>
    </div>
  );
};

const calcItem = (item, isGST) => {
  const qty = n(item.quantity), price = n(item.price), disc = n(item.discount_percent), gstPct = n(item.gst_percent);
  const inclusive = !!item.gst_inclusive;
  const gross = qty * price;
  const discAmt = gross * disc / 100;
  const afterDisc = gross - discAmt;
  if (isGST && inclusive && gstPct > 0) {
    const taxable = afterDisc / (1 + gstPct / 100);
    const gstAmt = afterDisc - taxable;
    return { ...item, _gross: gross, _discAmt: discAmt, _taxable: taxable, _gstAmt: gstAmt, total: afterDisc };
  }
  const taxable = afterDisc;
  const gstAmt = isGST ? taxable * gstPct / 100 : 0;
  return { ...item, _gross: gross, _discAmt: discAmt, _taxable: taxable, _gstAmt: gstAmt, total: taxable + gstAmt };
};

/* Helper: recalculate per-item values for invoice templates (same logic as calcItem) */
const calcInvItem = (item, isGST) => {
  const qty = n(item.quantity), price = n(item.price), disc = n(item.discount_percent), gstPct = n(item.gst_percent);
  const inclusive = !!item.gst_inclusive;
  const gross = qty * price;
  const discAmt = gross * disc / 100;
  const afterDisc = gross - discAmt;
  if (isGST && inclusive && gstPct > 0) {
    const taxable = afterDisc / (1 + gstPct / 100);
    const gstAmt = afterDisc - taxable;
    return { taxable, gstAmt, total: afterDisc };
  }
  const taxable = afterDisc;
  const gstAmt = isGST ? taxable * gstPct / 100 : 0;
  return { taxable, gstAmt, total: taxable + gstAmt };
};

/* ── Photo Lightbox — fullscreen viewer for invoice photos ── */
const PhotoLightbox = ({ photos, initialIndex, onClose }) => {
  const [idx, setIdx] = React.useState(initialIndex || 0);
  if (!photos || photos.length === 0) return null;
  const src = typeof photos[idx] === 'string' ? photos[idx] : photos[idx]?.url;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}>
      <button onClick={onClose} style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer', zIndex: 10000, lineHeight: 1 }}>&times;</button>
      <div style={{ position: 'absolute', top: 14, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 13, fontWeight: 600, opacity: 0.7 }}>{idx + 1} / {photos.length}</div>
      <img src={src} alt="" onClick={e => e.stopPropagation()} style={{ maxWidth: '92vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8, cursor: 'default', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }} />
      {photos.length > 1 && (
        <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <button onClick={() => setIdx(i => (i - 1 + photos.length) % photos.length)} style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&lsaquo;</button>
          <button onClick={() => setIdx(i => (i + 1) % photos.length)} style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&rsaquo;</button>
        </div>
      )}
    </div>
  );
};

/* ── Print helper — opens a new window with only the invoice HTML ── */
const openPrintWindow = (rootId, pageSize = 'A4', margins = {}) => {
  const node = document.getElementById(rootId);
  if (!node) return;
  const html = node.innerHTML;
  const mt = margins.top ?? 8, mb = margins.bottom ?? 8, ml = margins.left ?? 8, mr = margins.right ?? 8;
  const w = window.open('', '_blank');
  if (!w) { alert('Pop-up blocked! Please allow pop-ups for this site and try again.'); return; }
  w.document.write(`<!DOCTYPE html><html><head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <base href="${window.location.origin}/">
    <title>Invoice</title>
    <style>
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; background: #fff; color: #000; }
      @page { margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; size: ${pageSize}; }
      @media print { html, body { width: 100%; } }
      img { max-width: 100%; }
    </style>
  </head><body>${html}</body></html>`);
  w.document.close();
  w.focus();
  // Wait for all images to load before printing
  const images = w.document.querySelectorAll('img');
  const imagePromises = Array.from(images).map(img => {
    if (img.complete) return Promise.resolve();
    return new Promise(resolve => { img.onload = resolve; img.onerror = resolve; });
  });
  Promise.all(imagePromises).then(() => {
    setTimeout(() => { try { w.print(); } catch(e) {} }, 300);
  });
};

/* ── Modern Invoice Template ───────────────── */
const ModernInvoice = ({ inv, shop }) => {
  const billLabel = BILL_LABELS[inv.bill_type] || 'INVOICE';
  const isGST = inv.bill_type === 'tax_invoice' || inv.bill_type === 'bill_of_supply';
  const logoSrc = shop.logo_url ? resolveMediaUrl(shop.logo_url) : logo;
  const items = inv.items || [];
  const hasDiscount = items.some(i => n(i.discount_percent) > 0);
  const hasHSN = items.some(i => i.hsn_sac);
  const hasDesc = items.some(i => i.description);
  // totals
  const grossSubtotal = items.reduce((s, i) => s + n(i.price) * n(i.quantity), 0);
  const totalDisc = items.reduce((s, i) => s + n(i._discAmt ?? (n(i.price) * n(i.quantity) * n(i.discount_percent) / 100)), 0);
  const taxable = n(inv.subtotal) || (grossSubtotal - totalDisc);
  const gstTotal = n(inv.gst_total);
  const grandTotal = n(inv.grand_total);
  const cgst = gstTotal / 2; const sgst = gstTotal / 2;
  const blue = '#1e3a5f'; const green = '#15803d';

  const colSpan = 3 + (hasHSN ? 1 : 0) + (hasDiscount ? 2 : 0) + (isGST ? 2 : 0);
  const partyAddress = billToAddress(inv);
  const deliveredAddress = deliveryAddressOf(inv);
  const partyPhone = billToPhone(inv);

  return (
    <div style={{ fontFamily: "'Segoe UI', Arial, sans-serif", fontSize: 12, color: '#111', background: '#fff', padding: 0 }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${blue}, #1d4ed8)`, color: '#fff', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <img src={logoSrc} alt="" style={{ width: 52, height: 52, borderRadius: 9, objectFit: 'cover', background: '#fff', padding: 2, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>{shop.shop_name || 'Smart Vyapari'}</div>
            {shop.gst_number && <div style={{ fontSize: 10, opacity: 0.85, marginTop: 2 }}>GSTIN: <strong>{shop.gst_number}</strong></div>}
            {shop.shop_address && <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2, maxWidth: 260 }}>{shop.shop_address}</div>}
            <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2 }}>
              {shop.shop_phone && <span>📞 {shop.shop_phone}</span>}
              {shop.shop_email && <span style={{ marginLeft: 10 }}>✉ {shop.shop_email}</span>}
            </div>
          </div>
        </div>
        <div style={{ textAlign: 'right', minWidth: 160 }}>
          <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: 1.5, color: '#fbbf24' }}>{billLabel}</div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 5 }}>Invoice No: <strong>#{inv.invoice_number}</strong></div>
          <div style={{ fontSize: 10, opacity: 0.75, marginTop: 2 }}>Date: {fmtDate(inv.invoice_date || inv.created_at)}</div>
          <div style={{ marginTop: 7, display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: inv.payment_status === 'paid' ? '#16a34a' : inv.payment_status === 'partial' ? '#d97706' : '#dc2626', color: '#fff' }}>
            {(inv.payment_status || 'UNPAID').toUpperCase()}
          </div>
        </div>
      </div>

      {/* Bill To + Delivered To */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ padding: '12px 20px', borderRight: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Bill To :</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>{inv.customer_name}</div>
          {partyPhone && <div style={{ fontSize: 11, color: '#374151', marginTop: 3 }}>📞 <strong>{partyPhone}</strong></div>}
          {inv.customer_gstin && <div style={{ fontSize: 11, color: '#374151', marginTop: 2 }}>GSTIN: {inv.customer_gstin}</div>}
          {partyAddress && <div style={{ fontSize: 11, color: '#4b5563', marginTop: 3, lineHeight: 1.4 }}>{partyAddress}</div>}
        </div>
        <div style={{ padding: '12px 20px' }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Delivered To :</div>
          {deliveredAddress ? (
            <div style={{ fontSize: 11, color: '#4b5563', lineHeight: 1.5 }}>{deliveredAddress}</div>
          ) : (
            <div style={{ fontSize: 11, color: '#6b7280' }}>-</div>
          )}
        </div>
      </div>

      {/* Payment Info */}
      <div style={{ padding: '10px 20px', borderBottom: '1px solid #e5e7eb', background: '#fafafa' }}>
        <div style={{ fontSize: 9, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Payment Info</div>
          {inv.payment_entries && inv.payment_entries.length > 0 ? (
            <>
              {inv.payment_entries.map((pe, i) => (
                <div key={i} style={{ fontSize: 11, marginBottom: 2 }}><strong>{pe.mode}:</strong> ₹{Number(pe.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}{pe.payment_date ? <span style={{ color: '#6b7280', marginLeft: 6, fontSize: 10 }}>({formatDateDMY(pe.payment_date)})</span> : null}</div>
              ))}
            </>
          ) : (
            <div style={{ fontSize: 11, marginBottom: 3 }}><strong>Mode:</strong> {inv.payment_mode}</div>
          )}
          <div style={{ fontSize: 11, marginBottom: 3 }}><strong>Status:</strong> {(inv.payment_status || '').toUpperCase()}</div>
          {inv.balance_amount > 0 && <div style={{ fontSize: 11, marginBottom: 3, color: '#dc2626' }}><strong>Balance:</strong> ₹{Number(inv.balance_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>}
          {inv.notes && <div style={{ fontSize: 11, color: '#4b5563', marginTop: 4, padding: '4px 8px', background: '#f9fafb', borderRadius: 5 }}><em>{inv.notes}</em></div>}
      </div>

      {/* Finance Details Block — printed below Bill To / Payment Info */}
      {inv.finance_company && (
        <div style={{ margin: '0 0 0', padding: '10px 20px', background: '#EFF6FF', borderBottom: '1px solid #BFDBFE', display: 'flex', flexWrap: 'wrap', gap: '6px 28px', alignItems: 'flex-start' }}>
          <div style={{ width: '100%', fontSize: 9, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>🏢 Finance / EMI Details</div>
          <div style={{ fontSize: 11 }}><strong>Company:</strong> {inv.finance_company}</div>
          {inv.finance_scheme && <div style={{ fontSize: 11 }}><strong>Scheme:</strong> {inv.finance_scheme}</div>}
          {inv.finance_amount && <div style={{ fontSize: 11 }}><strong>Financed:</strong> ₹{Number(inv.finance_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>}
          {inv.finance_processing_fee && <div style={{ fontSize: 11 }}><strong>Processing Fee:</strong> ₹{Number(inv.finance_processing_fee).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>}
          {inv.finance_emi_amount && <div style={{ fontSize: 11 }}><strong>EMI:</strong> ₹{Number(inv.finance_emi_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}/month</div>}
          {inv.finance_emi_tenure && <div style={{ fontSize: 11 }}><strong>Tenure:</strong> {inv.finance_emi_tenure} months</div>}
          {inv.finance_emi_date && <div style={{ fontSize: 11 }}><strong>Start EMI:</strong> {formatDateDMY(inv.finance_emi_date)}</div>}
          {inv.finance_emi_end_date && <div style={{ fontSize: 11 }}><strong>End EMI:</strong> {formatDateDMY(inv.finance_emi_end_date)}</div>}
          {inv.finance_product && <div style={{ fontSize: 11 }}><strong>Product:</strong> {inv.finance_product}</div>}
        </div>
      )}

      {/* Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
            <th style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', width: 32 }}>#</th>
            <th style={{ padding: '8px 8px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>Description</th>
            {hasHSN && <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 700, color: '#374151', width: 70 }}>HSN/SAC</th>}
            <th style={{ padding: '8px 8px', textAlign: 'center', fontWeight: 700, color: '#374151', width: 40 }}>Unit</th>
            <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: '#374151', width: 40 }}>Qty</th>
            <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: '#374151', width: 72 }}>Rate</th>
            {hasDiscount && <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: '#374151', width: 40 }}>Disc%</th>}
            {hasDiscount && <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: '#374151', width: 72 }}>Taxable</th>}
            {isGST && <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: '#374151', width: 40 }}>GST%</th>}
            {isGST && <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 700, color: '#374151', width: 66 }}>GST Amt</th>}
            <th style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: '#374151', width: 76 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const { taxable: taxableVal, gstAmt, total } = calcInvItem(item, isGST);
            return (
              <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 ? '#fafafa' : '#fff' }}>
                <td style={{ padding: '8px 14px', color: '#6b7280', fontSize: 11 }}>{i + 1}</td>
                <td style={{ padding: '8px 8px' }}>
                  <div style={{ fontWeight: 600, color: '#111' }}>{item.product_name}</div>
                  {item.description && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 1 }}>{item.description}</div>}
                </td>
                {hasHSN && <td style={{ padding: '8px 8px', textAlign: 'center', color: '#6b7280', fontFamily: 'monospace', fontSize: 10 }}>{item.hsn_sac || '—'}</td>}
                <td style={{ padding: '8px 8px', textAlign: 'center', color: '#374151' }}>{item.unit || 'Pcs'}</td>
                <td style={{ padding: '8px 8px', textAlign: 'right' }}>{item.quantity}</td>
                <td style={{ padding: '8px 8px', textAlign: 'right' }}>₹{fmtNum(item.price)}</td>
                {hasDiscount && <td style={{ padding: '8px 8px', textAlign: 'right', color: '#d97706' }}>{n(item.discount_percent) > 0 ? `${item.discount_percent}%` : '—'}</td>}
                {hasDiscount && <td style={{ padding: '8px 8px', textAlign: 'right', color: '#374151' }}>₹{fmtNum(taxableVal)}</td>}
                {isGST && <td style={{ padding: '8px 8px', textAlign: 'right', color: '#6b7280' }}>{item.gst_percent}%</td>}
                {isGST && <td style={{ padding: '8px 8px', textAlign: 'right', color: '#6b7280' }}>₹{fmtNum(gstAmt)}</td>}
                <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: green }}>₹{fmtNum(total)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Totals */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '2px solid #e2e8f0', padding: '14px 20px 14px 0' }}>
        <table style={{ width: 250, borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ padding: '3px 0', color: '#6b7280', fontSize: 12 }}>Gross Amount</td>
              <td style={{ textAlign: 'right', fontSize: 12 }}>₹{fmtNum(grossSubtotal)}</td>
            </tr>
            {totalDisc > 0 && (
              <tr>
                <td style={{ padding: '3px 0', color: '#d97706', fontSize: 12 }}>Less: Discount</td>
                <td style={{ textAlign: 'right', fontSize: 12, color: '#d97706' }}>-₹{fmtNum(totalDisc)}</td>
              </tr>
            )}
            {(hasDiscount || isGST) && (
              <tr style={{ borderTop: '1px solid #e5e7eb' }}>
                <td style={{ padding: '4px 0', color: '#374151', fontSize: 12, fontWeight: 600 }}>Taxable Amount</td>
                <td style={{ textAlign: 'right', fontSize: 12, fontWeight: 600 }}>₹{fmtNum(taxable)}</td>
              </tr>
            )}
            {isGST && (
              <>
                <tr>
                  <td style={{ padding: '2px 0', color: '#6b7280', fontSize: 11 }}>CGST</td>
                  <td style={{ textAlign: 'right', fontSize: 11 }}>₹{fmtNum(cgst)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 0', color: '#6b7280', fontSize: 11 }}>SGST</td>
                  <td style={{ textAlign: 'right', fontSize: 11 }}>₹{fmtNum(sgst)}</td>
                </tr>
                <tr>
                  <td style={{ padding: '2px 0', color: '#6b7280', fontSize: 11 }}>Total GST</td>
                  <td style={{ textAlign: 'right', fontSize: 11 }}>₹{fmtNum(gstTotal)}</td>
                </tr>
              </>
            )}
            <tr style={{ borderTop: '2px solid #1e3a5f' }}>
              <td style={{ padding: '7px 0 0', fontSize: 15, fontWeight: 800, color: '#111' }}>Grand Total</td>
              <td style={{ textAlign: 'right', fontSize: 17, fontWeight: 900, color: green, paddingTop: 7 }}>₹{fmtNum(grandTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Bank Details + UPI QR */}
      {(shop.bank_name || shop.bank_account || shop.upi_id) && (
        <div style={{ margin: '0 16px 14px', padding: '10px 14px', background: '#eff6ff', borderRadius: 7, borderLeft: '4px solid #3b82f6', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Bank Details</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 20px', fontSize: 11 }}>
              {shop.bank_name && <span><strong>Bank:</strong> {shop.bank_name}</span>}
              {shop.bank_account_name && <span><strong>Name:</strong> {shop.bank_account_name}</span>}
              {shop.bank_account && <span><strong>A/c:</strong> {shop.bank_account}</span>}
              {shop.bank_ifsc && <span><strong>IFSC:</strong> {shop.bank_ifsc}</span>}
              {shop.bank_branch && <span><strong>Branch:</strong> {shop.bank_branch}</span>}
              {shop.upi_id && <span><strong>UPI:</strong> {shop.upi_id}</span>}
            </div>
          </div>
          {shop.upi_id && <UpiQrCode upiId={shop.upi_id} shopName={shop.shop_name} amount={grandTotal} size={90} />}
        </div>
      )}

      {/* Terms */}
      {shop.terms_conditions && (
        <div style={{ margin: '0 16px 14px', padding: '10px 14px', background: '#f9fafb', borderRadius: 7, border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Terms & Conditions</div>
          <div style={{ fontSize: 10, color: '#6b7280', whiteSpace: 'pre-line', lineHeight: 1.5 }}>{shop.terms_conditions}</div>
        </div>
      )}

      {/* Signature */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 20px 10px' }}>
        <div style={{ textAlign: 'right' }}>
          {shop.signature_url ? (
            <img src={resolveMediaUrl(shop.signature_url)} alt="Signature" style={{ width: 140, height: 42, objectFit: 'contain', marginBottom: 5 }} />
          ) : (
            <div style={{ width: 140, height: 42, borderBottom: '1px solid #ccc', marginBottom: 5 }}></div>
          )}
          <div style={{ fontSize: 11, fontWeight: 'bold', color: '#374151' }}>For, {shop.shop_name || ''}</div>
        </div>
      </div>

      {/* Photo Attachments */}
      {(() => {
        const invoicePhotos = (() => { try { const p = typeof inv.photos === 'string' ? JSON.parse(inv.photos) : inv.photos; return Array.isArray(p) ? p : []; } catch { return []; } })();
        return invoicePhotos.length > 0 ? (
          <div style={{ margin: '0 16px 14px', padding: '10px 14px', background: '#f9fafb', borderRadius: 7, border: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Product Photos</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {invoicePhotos.map((url, i) => (
                <img key={i} src={resolveMediaUrl(url)} alt={`Attachment ${i+1}`} crossOrigin="anonymous" data-lightbox-index={i} data-lightbox-group="invoice-photos" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', cursor: 'pointer' }} title="Click to view full size" />
              ))}
            </div>
          </div>
        ) : null;
      })()}

      {/* Footer */}
      <div style={{ padding: '10px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', background: '#f8fafc' }}>
        <div style={{ fontSize: 10, color: '#9ca3af' }}>Computer generated invoice — Smart Vyapari</div>
        <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600 }}>Thank you for your business! 🙏</div>
      </div>
    </div>
  );
};

/* ── Classic / A4 Invoice Template ────────── */
const ClassicInvoice = ({ inv, shop }) => {
  const logoSrc = shop.logo_url ? resolveMediaUrl(shop.logo_url) : logo;
  const billLabel = BILL_LABELS[inv.bill_type] || 'INVOICE';
  const isGST = inv.bill_type === 'tax_invoice' || inv.bill_type === 'bill_of_supply';
  const items = inv.items || [];
  const hasHSN = items.some(i => i.hsn_sac);
  const hasDiscount = items.some(i => n(i.discount_percent) > 0);

  const subtotal = n(inv.subtotal);
  const gstTotal = n(inv.gst_total);
  const grandTotal = n(inv.grand_total);
  const displayTotal = Math.round(grandTotal);
  const roundOff = displayTotal - grandTotal;
  const totalQty = items.reduce((s, i) => s + n(i.quantity), 0);
  const totalTaxable = items.reduce((s, i) => s + calcInvItem(i, isGST).taxable, 0);

  // Valid Until for quotations (+10 days)
  const validUntilDate = inv.bill_type === 'quotation' ? (() => {
    const d = new Date(inv.invoice_date || inv.created_at || Date.now()); d.setDate(d.getDate()+10); return fmtDMY(d);
  })() : null;

  // GST summary grouped by HSN/SAC + rate
  const gstGroupMap = {};
  items.forEach(item => {
    const rate = n(item.gst_percent); if (!rate) return;
    const hsn = item.hsn_sac || ''; const key = `${hsn}_${rate}`;
    const taxable = calcInvItem(item, isGST).taxable;
    if (!gstGroupMap[key]) gstGroupMap[key] = { hsn, rate, taxable: 0 };
    gstGroupMap[key].taxable += taxable;
  });
  const gstRows = Object.values(gstGroupMap);
  const totalCGST = gstRows.reduce((s, r) => s + r.taxable * r.rate / 200, 0);

  // colCount: S.No + PARTICULARS + [HSN] + QTY + UNIT PRICE + [DISC] + [GST] + AMOUNT
  const colCount = 5 + (hasHSN?1:0) + (hasDiscount?1:0) + (isGST?1:0);
  const totalLabelSpan = 2 + (hasHSN?1:0); // S.No + PARTICULARS + optional HSN

  const bdr = '1px solid #bbb';
  const thS = { border: bdr, padding: '5px 7px', textAlign: 'center', background: '#f2f2f2', fontWeight: 'bold', fontSize: 10.5 };
  const tdS = { border: bdr, padding: '5px 7px', fontSize: 10.5, verticalAlign: 'top' };
  const partyAddress = billToAddress(inv);
  const deliveredAddress = deliveryAddressOf(inv);
  const partyPhone = billToPhone(inv);

  return (
    <div style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 11, color: '#000', background: '#fff', padding: '4mm 5mm', boxSizing: 'border-box' }}>
      {/* ── Document Title ── */}
      <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 13, padding: '5px 0 6px', border: bdr, borderBottom: 'none', letterSpacing: 2, textTransform: 'uppercase' }}>
        {billLabel}
      </div>

      {/* ── Header: Shop Info | Invoice Details ── */}
      <div style={{ display: 'flex', border: bdr, borderBottom: 'none' }}>
        <div style={{ flex: 2.2, padding: '10px 12px', display: 'flex', gap: 12, alignItems: 'flex-start', borderRight: bdr }}>
          <img src={logoSrc} alt="" style={{ width: 64, height: 64, objectFit: 'contain', flexShrink: 0 }}
            onError={e => { e.target.onerror = null; e.target.style.display='none'; }} />
          <div>
            <div style={{ fontWeight: 'bold', fontSize: 15, marginBottom: 3 }}>{shop.shop_name || 'YOUR SHOP'}</div>
            {shop.shop_address && <div style={{ fontSize: 10, lineHeight: 1.5 }}>{shop.shop_address}</div>}
            {shop.shop_phone && <div style={{ fontSize: 10 }}>Contact : {shop.shop_phone}</div>}
            {shop.shop_email && <div style={{ fontSize: 10 }}>Email : {shop.shop_email}</div>}
            {shop.gst_number && <div style={{ fontSize: 10, marginTop: 3 }}>GSTIN : <strong>{shop.gst_number}</strong></div>}
          </div>
        </div>
        <div style={{ flex: 1, padding: '10px 12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 1, columnGap: 8 }}>
            <div style={{ color: '#555', fontSize: 10 }}>{inv.bill_type === 'quotation' ? 'Quotation No.' : 'Invoice No.'}</div>
            <div style={{ color: '#555', fontSize: 10 }}>Date</div>
            <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{inv.invoice_number}</div>
            <div style={{ fontWeight: 'bold', marginBottom: 6 }}>{fmtDMY(inv.invoice_date || inv.created_at)}</div>
            {validUntilDate && <>
              <div style={{ color: '#555', fontSize: 10, gridColumn: '1/-1' }}>Valid Until</div>
              <div style={{ fontWeight: 'bold', gridColumn: '1/-1' }}>{validUntilDate}</div>
            </>}
          </div>
        </div>
      </div>

      {/* ── Bill To ── */}
      <div style={{ display: 'flex', border: bdr, borderBottom: 'none' }}>
        <div style={{ flex: 1, padding: '8px 12px', borderRight: bdr, minHeight: 70 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 11 }}>Bill To :</div>
          {inv.customer_name && <div style={{ fontWeight: 'bold', fontSize: 11.5 }}>{inv.customer_name}</div>}
          {partyAddress && <div style={{ fontSize: 10, lineHeight: 1.5, marginTop: 2 }}>{partyAddress}</div>}
          {inv.customer_gstin && <div style={{ fontSize: 10, marginTop: 2 }}>GSTIN : <strong>{inv.customer_gstin}</strong></div>}
        </div>
        <div style={{ flex: 1, padding: '8px 12px', minHeight: 70 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4, fontSize: 11 }}>Delivered To :</div>
          {deliveredAddress ? (
            <div style={{ fontSize: 10, lineHeight: 1.5 }}>{deliveredAddress}</div>
          ) : (
            <div style={{ fontSize: 10, color: '#666' }}>-</div>
          )}
        </div>
      </div>

      {/* ── Contact / PoS Row ── */}
      <div style={{ border: bdr, borderBottom: 'none', padding: '4px 12px', display: 'flex', justifyContent: 'space-between', fontSize: 10 }}>
        <span><strong>Contact:</strong> {partyPhone ? <strong>{partyPhone}</strong> : ''}</span>
        <span><strong>PoS:</strong></span>
      </div>

      {/* ── Items Table ── */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...thS, width: 32 }}>S.No.</th>
            <th style={{ ...thS, textAlign: 'left' }}>PARTICULARS</th>
            {hasHSN && <th style={{ ...thS, width: 74 }}>HSN/SAC</th>}
            <th style={{ ...thS, width: 62 }}>QTY</th>
            <th style={{ ...thS, width: 90 }}>UNIT PRICE</th>
            {hasDiscount && <th style={{ ...thS, width: 52 }}>DISC%</th>}
            {isGST && <th style={{ ...thS, width: 45 }}>GST</th>}
            <th style={{ ...thS, width: 92 }}>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const { taxable } = calcInvItem(item, isGST);
            const itemName = item.product_name || item.item_name || item.name || '';
            const qty = n(item.quantity), price = n(item.price), disc = n(item.discount_percent);
            return (
              <React.Fragment key={idx}>
                <tr>
                  <td style={{ ...tdS, textAlign: 'center' }}>{idx+1}</td>
                  <td style={{ ...tdS, fontWeight: 600 }}>
                    {itemName}
                    {item.description && <div style={{ fontSize: 9.5, fontWeight: 'normal', color: '#555', marginTop: 1 }}>{item.description}</div>}
                  </td>
                  {hasHSN && <td style={{ ...tdS, textAlign: 'center' }}>{item.hsn_sac || ''}</td>}
                  <td style={{ ...tdS, textAlign: 'center' }}>{qty} {item.unit || 'Pcs'}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(price)}</td>
                  {hasDiscount && <td style={{ ...tdS, textAlign: 'center' }}>{disc > 0 ? `${disc}%` : '-'}</td>}
                  {isGST && <td style={{ ...tdS, textAlign: 'center' }}>{n(item.gst_percent)}%</td>}
                  <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(taxable)}</td>
                </tr>
              </React.Fragment>
            );
          })}
          {/* TOTAL row */}
          <tr style={{ background: '#fafafa' }}>
            <td colSpan={totalLabelSpan} style={{ ...tdS, fontWeight: 'bold' }}>TOTAL</td>
            <td style={{ ...tdS, textAlign: 'center', fontWeight: 'bold' }}>{totalQty}</td>
            <td style={{ ...tdS }}></td>
            {hasDiscount && <td style={tdS}></td>}
            {isGST && <td style={tdS}></td>}
            <td style={{ ...tdS, textAlign: 'right', fontWeight: 'bold' }}>₹ {fmtNum(subtotal || totalTaxable)}</td>
          </tr>
        </tbody>
      </table>

      {/* ── Delivery Terms + Totals ── */}
      <div style={{ display: 'flex', border: bdr, borderTop: 'none' }}>
        <div style={{ flex: 1, padding: '8px 12px', borderRight: bdr }}>
          <div style={{ fontWeight: 'bold', fontSize: 10.5 }}>Payment Details :</div>
          {inv.payment_entries && inv.payment_entries.length > 0 ? (
            inv.payment_entries.map((pe, i) => (
              <div key={i} style={{ fontSize: 10, marginTop: 2 }}>{pe.mode}: ₹ {Number(pe.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}{pe.payment_date ? <span style={{ color: '#666', marginLeft: 4, fontSize: 9 }}>({formatDateDMY(pe.payment_date)})</span> : null}</div>
            ))
          ) : (
            <div style={{ fontSize: 10, marginTop: 2 }}>Mode: {inv.payment_mode}</div>
          )}
          <div style={{ fontSize: 10, marginTop: 2 }}>Status: {(inv.payment_status || '').toUpperCase()}</div>
          {inv.balance_amount > 0 && <div style={{ fontSize: 10, marginTop: 2, color: '#dc2626' }}>Balance: ₹ {Number(inv.balance_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>}
          {inv.finance_company && (
            <div style={{ marginTop: 6, paddingTop: 5, borderTop: '1px dashed #ccc' }}>
              <div style={{ fontWeight: 'bold', fontSize: 10.5, marginBottom: 2 }}>Finance Details :</div>
              <div style={{ fontSize: 10 }}>Company: {inv.finance_company}</div>
              {inv.finance_scheme && <div style={{ fontSize: 10 }}>Scheme: {inv.finance_scheme}</div>}
              {inv.finance_amount && <div style={{ fontSize: 10 }}>Amount: ₹ {Number(inv.finance_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>}
              {inv.finance_emi_amount && <div style={{ fontSize: 10 }}>EMI: ₹ {Number(inv.finance_emi_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}/month</div>}
              {inv.finance_emi_tenure && <div style={{ fontSize: 10 }}>Tenure: {inv.finance_emi_tenure} months</div>}
              {inv.finance_emi_date && <div style={{ fontSize: 10 }}>Start: {formatDateDMY(inv.finance_emi_date)}</div>}
              {inv.finance_emi_end_date && <div style={{ fontSize: 10 }}>End: {formatDateDMY(inv.finance_emi_end_date)}</div>}
            </div>
          )}
        </div>
        <div style={{ flex: 1, padding: '8px 12px' }}>
          <table style={{ width: '100%', fontSize: 11.5 }}>
            <tbody>
              <tr><td style={{ paddingBottom: 2 }}>Sub Total</td><td style={{ textAlign: 'right', paddingBottom: 2 }}>₹ {fmtNum(subtotal || totalTaxable)}</td></tr>
              {isGST && <tr><td style={{ paddingBottom: 2 }}>Tax Amount (+)</td><td style={{ textAlign: 'right', paddingBottom: 2 }}>₹ {fmtNum(gstTotal)}</td></tr>}
              {Math.abs(roundOff) >= 0.005 && <tr><td style={{ paddingBottom: 2 }}>Round Off ({roundOff >= 0 ? '+' : '-'})</td><td style={{ textAlign: 'right', paddingBottom: 2 }}>₹ {fmtNum(Math.abs(roundOff))}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Amount in Words + Total Amount ── */}
      <div style={{ display: 'flex', border: bdr, borderTop: 'none' }}>
        <div style={{ flex: 2, padding: '7px 12px', borderRight: bdr }}>
          <div style={{ fontWeight: 'bold', fontSize: 10.5, marginBottom: 3 }}>Amount in Words :</div>
          <div style={{ fontSize: 10.5 }}>Amount (in words) : Rupees {numToWords(displayTotal)} Only</div>
        </div>
        <div style={{ flex: 1, padding: '7px 12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ fontSize: 10.5, color: '#444', marginBottom: 2 }}>TOTAL AMOUNT</div>
          <div style={{ fontWeight: 'bold', fontSize: 15 }}>₹ {displayTotal.toLocaleString('en-IN')}.00</div>
        </div>
      </div>

      {/* ── CGST / SGST Summary Table ── */}
      {isGST && gstRows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', border: bdr, borderTop: 'none' }}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ ...thS, textAlign: 'left', width: 80 }}>HSN/SAC</th>
              <th rowSpan={2} style={{ ...thS }}>Taxable Amount</th>
              <th colSpan={2} style={{ ...thS }}>CGST</th>
              <th colSpan={2} style={{ ...thS }}>SGST</th>
              <th rowSpan={2} style={{ ...thS }}>Total Tax Amount</th>
            </tr>
            <tr>
              <th style={{ ...thS, fontWeight: 600 }}>Rate</th>
              <th style={{ ...thS, fontWeight: 600 }}>Amount</th>
              <th style={{ ...thS, fontWeight: 600 }}>Rate</th>
              <th style={{ ...thS, fontWeight: 600 }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {gstRows.map((row, i) => {
              const cgst = row.taxable * row.rate / 200;
              return (
                <tr key={i}>
                  <td style={{ ...tdS, textAlign: 'left' }}>{row.hsn || ''}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(row.taxable)}</td>
                  <td style={{ ...tdS, textAlign: 'center' }}>{row.rate/2}%</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(cgst)}</td>
                  <td style={{ ...tdS, textAlign: 'center' }}>{row.rate/2}%</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(cgst)}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(row.taxable * row.rate / 100)}</td>
                </tr>
              );
            })}
            <tr style={{ background: '#fafafa', fontWeight: 'bold' }}>
              <td style={{ ...tdS }}>Total</td>
              <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(gstRows.reduce((s,r)=>s+r.taxable,0))}</td>
              <td style={tdS}></td>
              <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(totalCGST)}</td>
              <td style={tdS}></td>
              <td style={{ ...tdS, textAlign: 'right' }}>₹ {fmtNum(totalCGST)}</td>
              <td style={{ ...tdS, textAlign: 'right' }}>{fmtNum(totalCGST * 2)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {/* ── Terms / Bank Details + Signature ── */}
      <div style={{ display: 'flex', border: bdr, borderTop: 'none' }}>
        <div style={{ flex: 1.5, padding: '8px 12px', borderRight: bdr }}>
          <div style={{ fontWeight: 'bold', fontSize: 10.5, marginBottom: 4 }}>Terms / Declaration</div>
          {shop.terms_conditions && (
            <div style={{ fontSize: 9.5, color: '#333', whiteSpace: 'pre-line', lineHeight: 1.5, marginBottom: 8 }}>
              {shop.terms_conditions}
            </div>
          )}
          {(shop.bank_name || shop.bank_account) && (
            <>
              <div style={{ fontWeight: 'bold', fontSize: 10.5, marginBottom: 3 }}>Bank Details -</div>
              {shop.bank_name && <div style={{ fontSize: 10 }}>Bank Name &nbsp;&nbsp; : {shop.bank_name}</div>}
              {shop.bank_account && <div style={{ fontSize: 10 }}>Account No. &nbsp; : {shop.bank_account}</div>}
              {(shop.bank_branch || shop.bank_ifsc) && <div style={{ fontSize: 10 }}>Branch & IFSC : {[shop.bank_branch, shop.bank_ifsc].filter(Boolean).join(' : ')}</div>}
              {shop.upi_id && <div style={{ fontSize: 10 }}>UPI &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;: {shop.upi_id}</div>}
            </>
          )}
        </div>
        <div style={{ flex: 1, padding: '8px 12px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          {shop.upi_id && <UpiQrCode upiId={shop.upi_id} shopName={shop.shop_name} amount={grandTotal} size={85} />}
          <div style={{ textAlign: 'right' }}>
            {shop.signature_url ? (
              <img src={resolveMediaUrl(shop.signature_url)} alt="Signature" style={{ width: 140, height: 42, objectFit: 'contain', marginBottom: 5 }} />
            ) : (
              <div style={{ width: 140, height: 42, borderBottom: '1px solid #999', marginBottom: 5 }}></div>
            )}
            <div style={{ fontSize: 11, fontWeight: 'bold' }}>For, {shop.shop_name || ''}</div>
          </div>
        </div>
      </div>

      {/* ── Photo Attachments ── */}
      {(() => {
        const invoicePhotos = (() => { try { const p = typeof inv.photos === 'string' ? JSON.parse(inv.photos) : inv.photos; return Array.isArray(p) ? p : []; } catch { return []; } })();
        return invoicePhotos.length > 0 ? (
          <div style={{ border: bdr, padding: '8px 12px', marginTop: 6 }}>
            <div style={{ fontWeight: 'bold', fontSize: 10.5, marginBottom: 6 }}>Product Photos</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {invoicePhotos.map((url, i) => (
                <img key={i} src={resolveMediaUrl(url)} alt={`Attachment ${i+1}`} crossOrigin="anonymous" data-lightbox-index={i} data-lightbox-group="invoice-photos" style={{ width: 110, height: 110, objectFit: 'cover', border: '1px solid #ccc', cursor: 'pointer' }} title="Click to view full size" />
              ))}
            </div>
          </div>
        ) : null;
      })()}

      {/* ── Footer ── */}
      <div style={{ textAlign: 'right', fontSize: 9, color: '#999', marginTop: 5 }}>
        Powered by Smart Vyapari BillSoft
      </div>
    </div>
  );
};

/* ── Thermal Invoice Template ──────────────── */
const ThermalInvoice = ({ inv, shop }) => {
  const billLabel = BILL_LABELS[inv.bill_type] || 'INVOICE';
  const isGST = inv.bill_type === 'tax_invoice' || inv.bill_type === 'bill_of_supply';
  const logoSrc = shop.logo_url ? resolveMediaUrl(shop.logo_url) : logo;
  const items = inv.items || [];
  const gstTotal = n(inv.gst_total);
  const grandTotal = n(inv.grand_total);
  const partyAddress = billToAddress(inv);
  const deliveredAddress = deliveryAddressOf(inv);
  const partyPhone = billToPhone(inv);
  const line = () => <div style={{ borderTop: '1px dashed #000', margin: '5px 0' }} />;
  return (
    <div style={{ fontFamily: 'Courier New, monospace', fontSize: 12, color: '#000', background: '#fff', width: 320, padding: '12px 14px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: 6 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
          <img src={logoSrc} alt="" style={{ width: 56, height: 56, borderRadius: 8, objectFit: 'cover', display: 'block' }} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 'bold', marginTop: 4 }}>{shop.shop_name || 'Smart Vyapari'}</div>
        {shop.gst_number && <div style={{ fontSize: 9 }}>GSTIN: {shop.gst_number}</div>}
        {shop.shop_address && <div style={{ fontSize: 9 }}>{shop.shop_address}</div>}
        {shop.shop_phone && <div style={{ fontSize: 9 }}>📞 {shop.shop_phone}</div>}
      </div>
      {line()}
      <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 13 }}>{billLabel}</div>
      <div style={{ fontSize: 10 }}>No: {inv.invoice_number} | {fmtDate(inv.invoice_date || inv.created_at)}</div>
      {line()}
      <div style={{ fontSize: 10 }}><strong>Bill To :</strong> <strong>{inv.customer_name}</strong></div>
      {partyAddress && <div style={{ fontSize: 9, lineHeight: 1.4 }}>{partyAddress}</div>}
      {partyPhone && <div style={{ fontSize: 9 }}>📞 <strong>{partyPhone}</strong></div>}
      <div style={{ fontSize: 10, marginTop: 4 }}><strong>Delivered To :</strong> {deliveredAddress || '-'}</div>
      {inv.customer_gstin && <div style={{ fontSize: 9 }}>GSTIN: {inv.customer_gstin}</div>}
      {line()}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '0 6px', fontSize: 9, fontWeight: 'bold', marginBottom: 3 }}>
        <span>Item</span><span>Qty×Rate</span><span>Amt</span>
      </div>
      {line()}
      {items.map((item, i) => {
        const { taxable: taxable2, gstAmt } = calcInvItem(item, isGST);
        return (
          <div key={i} style={{ fontSize: 10, marginBottom: 5 }}>
            <div style={{ fontWeight: 600 }}>{item.product_name}</div>
            {item.hsn_sac && <div style={{ fontSize: 9, color: '#555' }}>HSN: {item.hsn_sac}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>{item.quantity} {item.unit} × ₹{fmtNum(item.price)}{n(item.discount_percent) > 0 ? ` (-${item.discount_percent}%)` : ''}</span>
              <span>₹{fmtNum(taxable2 + gstAmt)}</span>
            </div>
            {isGST && <div style={{ fontSize: 9, color: '#666' }}>GST {item.gst_percent}%: ₹{fmtNum(gstAmt)}</div>}
          </div>
        );
      })}
      {line()}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}><span>Subtotal</span><span>₹{fmtNum(inv.subtotal)}</span></div>
      {isGST && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10 }}><span>GST (CGST+SGST)</span><span>₹{fmtNum(gstTotal)}</span></div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 'bold', borderTop: '1px solid #000', marginTop: 3, paddingTop: 4 }}>
        <span>TOTAL</span><span>₹{fmtNum(grandTotal)}</span>
      </div>
      {line()}
      <div style={{ fontSize: 10, textAlign: 'center' }}>
        {inv.payment_entries && inv.payment_entries.length > 0 ? (
          inv.payment_entries.map((pe, i) => <span key={i}>{i > 0 ? ' + ' : ''}{pe.mode}: ₹{Number(pe.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>)
        ) : (
          <span>Mode: {inv.payment_mode}</span>
        )}
        {' | '}{(inv.payment_status || '').toUpperCase()}
        {inv.balance_amount > 0 && <div style={{ color: '#dc2626', fontWeight: 600 }}>Balance: ₹{Number(inv.balance_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>}
      </div>
      {inv.finance_company && (
        <>
          {line()}
          <div style={{ fontSize: 9 }}>
            <div style={{ fontWeight: 'bold', marginBottom: 2 }}>Finance: {inv.finance_company}</div>
            {inv.finance_scheme && <div>Scheme: {inv.finance_scheme}</div>}
            {inv.finance_emi_amount && <div>EMI: ₹{Number(inv.finance_emi_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}/mo × {inv.finance_emi_tenure || '?'} months</div>}
            {inv.finance_emi_date && <div>Start: {formatDateDMY(inv.finance_emi_date)} → End: {inv.finance_emi_end_date ? formatDateDMY(inv.finance_emi_end_date) : '—'}</div>}
          </div>
        </>
      )}
      {shop.upi_id && (
        <div style={{ textAlign: 'center', marginTop: 4 }}>
          <UpiQrCode upiId={shop.upi_id} shopName={shop.shop_name} amount={grandTotal} size={120} />
        </div>
      )}
      {!shop.upi_id && shop.bank_account && <div style={{ fontSize: 9, textAlign: 'center' }}>A/c: {shop.bank_account} ({shop.bank_ifsc})</div>}
      {/* Photo Attachments */}
      {(() => {
        const invoicePhotos = (() => { try { const p = typeof inv.photos === 'string' ? JSON.parse(inv.photos) : inv.photos; return Array.isArray(p) ? p : []; } catch { return []; } })();
        return invoicePhotos.length > 0 ? (
          <>
            {line()}
            <div style={{ fontSize: 9, fontWeight: 'bold', marginBottom: 4 }}>Product Photos</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
              {invoicePhotos.map((url, i) => (
                <img key={i} src={url} alt={`Photo ${i+1}`} crossOrigin="anonymous" data-lightbox-index={i} data-lightbox-group="invoice-photos" style={{ width: 70, height: 70, objectFit: 'cover', border: '1px solid #ccc', cursor: 'pointer' }} title="Click to view full size" />
              ))}
            </div>
          </>
        ) : null;
      })()}
      {line()}
      <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 'bold' }}>Thank you! 🙏</div>
    </div>
  );
};

/* ── Print Modal ───────────────────────────── */
const FORMATS = [
  { id: 'classic', label: '📄 A4 Classic', desc: 'A4 professional layout' },
  { id: 'modern',  label: '🎨 Modern',     desc: 'Colorful gradient A4' },
  { id: 'thermal', label: '🖨 Thermal',    desc: '80mm receipt' },
];

const PrintModal = ({ invoice, shop, format, onFormatChange, onClose }) => {
  const Template = format === 'modern' ? ModernInvoice : format === 'classic' ? ClassicInvoice : ThermalInvoice;
  const shopPageSize = shop.page_size || 'A4';
  const shopOrientation = shop.page_orientation || 'portrait';
  const pageSize = format === 'thermal' ? '80mm 297mm' : (shopOrientation === 'landscape' ? `${shopPageSize} landscape` : shopPageSize);
  const margins = { top: shop.margin_top ?? 10, bottom: shop.margin_bottom ?? 10, left: shop.margin_left ?? 10, right: shop.margin_right ?? 10 };
  const [sending, setSending] = React.useState('');
  const [lightbox, setLightbox] = React.useState(null);

  // Parse invoice photos for lightbox
  const invoicePhotos = React.useMemo(() => {
    try { const p = typeof invoice.photos === 'string' ? JSON.parse(invoice.photos) : invoice.photos; return Array.isArray(p) ? p : []; } catch { return []; }
  }, [invoice.photos]);

  // Handle clicking on photo thumbnails inside the print preview
  const handlePrintAreaClick = (e) => {
    const img = e.target.closest('img[data-lightbox-group="invoice-photos"]');
    if (img) { setLightbox(parseInt(img.dataset.lightboxIndex, 10) || 0); }
  };

  const sendVia = async (channel) => {
    setSending(channel);
    try {
      await settingsAPI.sendInvoice({ invoice_id: invoice.id, channel });
      alert(`✅ Invoice sent via ${channel}!`);
    } catch (e) {
      alert(`❌ ${e.response?.data?.detail || e.message || `Failed to send via ${channel}`}`);
    }
    setSending('');
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '12px' }}>
      <div className="animate-scaleIn" style={{ background: '#f1f5f9', borderRadius: 14, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', marginBottom: 20, maxHeight: '98dvh', overflowY: 'auto' }}>
        <div style={{ background: '#1e293b', borderRadius: '14px 14px 0 0', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>🖨️ Print / Share Invoice — {invoice.invoice_number}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '14px 20px 8px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {FORMATS.map(f => (
            <button key={f.id} onClick={() => onFormatChange(f.id)}
              style={{ flex: 1, minWidth: 100, padding: '9px 8px', borderRadius: 9, border: `2px solid ${format === f.id ? '#3b82f6' : '#cbd5e1'}`, background: format === f.id ? '#eff6ff' : '#fff', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: format === f.id ? '#1d4ed8' : '#374151' }}>{f.label}</div>
              <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{f.desc}</div>
            </button>
          ))}
        </div>
        <div style={{ padding: '0 20px 14px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => openPrintWindow('sv-print-root', pageSize, margins)}
            style={{ padding: '9px 20px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>🖨️ Print</button>
          <button onClick={() => sendVia('whatsapp')} disabled={sending === 'whatsapp'}
            style={{ padding: '9px 16px', background: '#25D366', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: sending === 'whatsapp' ? 0.6 : 1 }}>
            {sending === 'whatsapp' ? '⏳' : '💬'} WhatsApp</button>
          <button onClick={() => sendVia('telegram')} disabled={sending === 'telegram'}
            style={{ padding: '9px 16px', background: '#0088cc', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: sending === 'telegram' ? 0.6 : 1 }}>
            {sending === 'telegram' ? '⏳' : '✈️'} Telegram</button>
          <button onClick={() => sendVia('email')} disabled={sending === 'email'}
            style={{ padding: '9px 16px', background: '#EA4335', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: sending === 'email' ? 0.6 : 1 }}>
            {sending === 'email' ? '⏳' : '📧'} Email</button>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{ padding: '9px 18px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Close</button>
        </div>
        <div id="sv-print-root" onClick={handlePrintAreaClick} style={{ background: '#fff', margin: '0 16px 16px', borderRadius: 8, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}>
          <Template inv={invoice} shop={shop} />
        </div>
      </div>
      {lightbox !== null && <PhotoLightbox photos={invoicePhotos} initialIndex={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
};

/* ── Purchase Bill Scan Modal ───────────────── */
const PurchaseBillScanModal = ({ onClose, onExtracted, darkMode, color }) => {
  const acc = color.swatch;
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const [preview, setPreview]   = useState(null);   // { dataUrl, file }
  const [scanning, setScanning] = useState(false);
  const [scanMsg,  setScanMsg]  = useState('');
  const [error,    setError]    = useState(null);

  const bg   = darkMode ? '#0f172a' : '#fff';
  const text = darkMode ? '#f1f5f9' : '#1e293b';
  const sub  = darkMode ? '#94a3b8' : '#64748b';

  const btn = (bg2, fg = '#fff', extra = {}) => ({
    padding: '14px 20px', background: bg2, color: fg, border: 'none', borderRadius: 12,
    fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center',
    justifyContent: 'center', gap: 10, width: '100%', ...extra,
  });

  // ── File chosen from camera or gallery ──────────────────────────────────
  const handleFileChosen = (file) => {
    if (!file) return;
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => setPreview({ dataUrl: e.target.result, file });
    reader.readAsDataURL(file);
  };

  // ── Send to backend for AI extraction ──────────────────────────────────
  const handleScan = async () => {
    if (!preview) return;
    setScanning(true);
    setError(null);
    setScanMsg('Uploading photo…');
    try {
      const formData = new FormData();
      formData.append('file', preview.file, preview.file.name || 'invoice.jpg');
      setScanMsg('Analysing with AI… (10–20 seconds)');
      const res = await supplierAPI.scanInvoice(formData);
      const { photo_url, extracted, error: aiErr } = res.data;
      if (aiErr) {
        // Keep modal open — show error so user can read it and decide to retry
        setError(aiErr);
        setScanning(false);
        return;
      }
      onExtracted({ photo_url, extracted });
    } catch (err) {
      setError(err.response?.data?.detail || 'Scan failed. Please try again.');
      setScanning(false);
    }
  };

  // ── No image chosen yet: show option buttons ────────────────────────────
  if (!preview) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 700, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
        <div style={{ background: bg, borderRadius: 20, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.35)' }}>
          {/* Header */}
          <div style={{ padding: '18px 20px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${darkMode ? '#1e293b' : '#e2e8f0'}` }}>
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: text }}>📷 Scan Invoice</div>
              <div style={{ fontSize: 12, color: sub, marginTop: 2 }}>Take a photo or upload from gallery</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: sub, lineHeight: 1 }}>×</button>
          </div>

          {/* Buttons */}
          <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Camera button */}
            <button
              onClick={() => cameraInputRef.current?.click()}
              style={btn(acc)}
            >
              <span style={{ fontSize: 22 }}>📸</span>
              <span>Take Photo with Camera</span>
            </button>

            {/* Gallery button */}
            <button
              onClick={() => galleryInputRef.current?.click()}
              style={btn(darkMode ? '#1e293b' : '#f1f5f9', darkMode ? '#f1f5f9' : '#1e293b', { border: `2px solid ${darkMode ? '#334155' : '#cbd5e1'}` })}
            >
              <span style={{ fontSize: 22 }}>🖼️</span>
              <span>Choose from Gallery / Files</span>
            </button>

            <div style={{ fontSize: 12, color: sub, textAlign: 'center', marginTop: 4 }}>
              Ensure good lighting · Keep invoice flat · All text must be visible
            </div>
          </div>
        </div>

        {/* Hidden file inputs */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          onChange={e => handleFileChosen(e.target.files?.[0])}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={e => handleFileChosen(e.target.files?.[0])}
        />
      </div>
    );
  }

  // ── Preview screen ──────────────────────────────────────────────────────
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 700, background: '#000', display: 'flex', flexDirection: 'column' }}>
      {/* Full-screen image preview */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
        <img
          src={preview.dataUrl}
          alt="Invoice to scan"
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />

        {/* AI scanning overlay */}
        {scanning && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
            <div style={{ width: 56, height: 56, border: '5px solid rgba(255,255,255,0.15)', borderTopColor: acc, borderRadius: '50%', animation: 'spin 0.85s linear infinite' }} />
            <div style={{ color: '#fff', fontSize: 16, fontWeight: 600, textAlign: 'center', padding: '0 40px' }}>{scanMsg || 'Processing…'}</div>
            <div style={{ color: '#94a3b8', fontSize: 13 }}>Do not close this screen</div>
          </div>
        )}

        {/* Error banner */}
        {error && !scanning && (
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(220,38,38,0.93)', padding: '14px 20px', textAlign: 'center', color: '#fff', fontSize: 14 }}>
            ⚠️ {error}
          </div>
        )}

        {/* Top close button */}
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff', fontSize: 20, width: 40, height: 40, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >×</button>
      </div>

      {/* Bottom action bar */}
      <div style={{ background: 'rgba(0,0,0,0.92)', padding: '16px 20px 28px', display: 'flex', gap: 12 }}>
        <button
          onClick={() => { setPreview(null); setError(null); setScanMsg(''); }}
          disabled={scanning}
          style={{ ...btn('#374151', '#fff', { flex: 1, opacity: scanning ? 0.5 : 1 }) }}
        >
          🔄 Retake
        </button>
        <button
          onClick={handleScan}
          disabled={scanning}
          style={{ ...btn(scanning ? '#475569' : acc, '#fff', { flex: 2 }) }}
        >
          {scanning ? '⏳ Scanning…' : '✅ Scan this Invoice'}
        </button>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

/* ── Purchase Bill List ─────────────────────── */
const PurchaseBillList = ({ onCreateNew, darkMode, color }) => {
  const { suppliers: sharedSuppliers, refreshSuppliers, refreshProducts } = useData();
  const [allBills, setAllBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editBill, setEditBill] = useState(null);
  const [editSupplier, setEditSupplier] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [payingBill, setPayingBill] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', mode: 'Cash', date: new Date().toISOString().split('T')[0] });
  const [paySaving, setPaySaving] = useState(false);
  const [deletingBillId, setDeletingBillId] = useState(null);
  const searchRef = useRef(null);
  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await supplierAPI.listAllBills({ search: search || undefined, status: statusFilter || undefined });
      const bills = r.data.bills || [];
      setAllBills(bills);
      setTotal(bills.length);
    } catch {}
    setLoading(false);
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => { try { const r = await supplierAPI.list({ page_size: 200 }); setSuppliers(r.data.suppliers || []); } catch {} })();
  }, []);

  const statusBadge = s => {
    const map = { paid: '#dcfce7:#166534', unpaid: '#fee2e2:#991b1b', partial: '#fef9c3:#854d0e' };
    const [bg, tx] = (map[s] || '#f1f5f9:#374151').split(':');
    return <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: bg, color: tx }}>{s}</span>;
  };

  const openPay = (b) => {
    const due = parseFloat(b.balance_amount || 0) > 0 ? b.balance_amount : b.grand_total;
    setPayingBill(b);
    setPayForm({ amount: String(parseFloat(due || 0).toFixed(2)), mode: b.payment_mode || 'Cash', date: new Date().toISOString().split('T')[0] });
  };
  const handleRecordBillPayment = async () => {
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) { alert('Enter a valid amount'); return; }
    setPaySaving(true);
    try {
      await paymentAPI.recordDirect({
        party_type: 'supplier',
        party_id: payingBill.supplier_id,
        party_name: payingBill.supplier_name,
        amount: amt,
        payment_mode: payForm.mode,
        payment_date: payForm.date,
        direction: 'out',
        description: `Payment for bill ${payingBill.bill_number || payingBill.id}`,
      });
      alert('Payment recorded successfully');
      setPayingBill(null);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Payment failed'); }
    setPaySaving(false);
  };

  const handleDeleteBillConfirmed = async () => {
    const id = deletingBillId;
    setDeletingBillId(null);
    if (!id) return;
    try {
      await supplierAPI.deleteBill(id);
      if (payingBill?.id === id) setPayingBill(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
  };

  const paged = allBills.slice((page - 1) * 15, page * 15);
  const totalPages = Math.ceil(total / 15);

  // Stats
  const totalAmount = allBills.reduce((s, b) => s + (parseFloat(b.grand_total) || 0), 0);
  const unpaidAmount = allBills.reduce((s, b) => s + (parseFloat(b.balance_amount) || 0), 0);

  return (
    <>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 18 }}>
        {[
          ['Total Bills', allBills.length, darkMode ? '#F1F5F9' : '#0F172A'],
          ['Total Amount', `₹${fmtNum(totalAmount)}`, '#16a34a'],
          ['Outstanding', `₹${fmtNum(unpaidAmount)}`, '#dc2626'],
        ].map(([label, val, clr]) => (
          <div key={label} style={{ ...card, padding: '14px 16px' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: clr }}>{val}</div>
            <div style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Search / Filter bar */}
      <div style={{ ...card, padding: 14, marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input ref={searchRef} style={{ ...inp, flex: 1, minWidth: 220 }} placeholder="Bill#, supplier, product, date, mode, notes…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select style={inp} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="paid">✓ Paid</option>
          <option value="unpaid">⏳ Unpaid</option>
          <option value="partial">🔶 Partial</option>
        </select>
        <button onClick={onCreateNew} style={{ padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
          ➕ New Purchase
        </button>
      </div>

      <div style={{ ...card, overflow: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50, color: darkMode ? '#64748B' : '#94A3B8' }}>Loading...</div>
        ) : paged.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 52, marginBottom: 10 }}>📦</div>
            <p style={{ color: darkMode ? '#64748B' : '#94A3B8', marginBottom: 14 }}>No purchase bills found</p>
            <button onClick={onCreateNew} style={{ padding: '9px 22px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Create First Purchase Bill</button>
          </div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 650 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                  {['Bill #', 'Supplier', 'Date', 'Items', 'Amount', 'Mode', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: h === '' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paged.map(b => (
                  <React.Fragment key={b.id}>
                  <tr style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: acc, whiteSpace: 'nowrap' }}>{b.bill_number || '—'}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: darkMode ? '#F1F5F9' : '#0F172A' }}>{b.supplier_name}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B', whiteSpace: 'nowrap' }}>{fmtDate(b.bill_date)}</td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>{b.items?.length || 0} items</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: darkMode ? '#F1F5F9' : '#0F172A', whiteSpace: 'nowrap' }}>
                      <div>₹{fmtNum(b.grand_total)}</div>
                      {parseFloat(b.balance_amount || 0) > 0 && (
                        <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 700 }}>Due ₹{fmtNum(b.balance_amount)}</div>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>{b.payment_mode}</td>
                    <td style={{ padding: '10px 12px' }}>{statusBadge(b.payment_status)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {b.payment_status !== 'paid' && (
                          <button onClick={() => payingBill?.id === b.id ? setPayingBill(null) : openPay(b)}
                            style={{ padding: '4px 10px', background: payingBill?.id === b.id ? '#94a3b8' : '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                            {payingBill?.id === b.id ? '✕' : '💳 Pay'}
                          </button>
                        )}
                        <button onClick={() => { setEditBill(b); setEditSupplier(suppliers.find(s => s.id === b.supplier_id) || { id: b.supplier_id, name: b.supplier_name }); setShowBillModal(true); }}
                          style={{ padding: '4px 10px', background: 'transparent', border: `1px solid ${acc}`, color: acc, borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>✏️ Edit</button>
                        <button onClick={() => setDeletingBillId(b.id)}
                          style={{ padding: '4px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                  {payingBill?.id === b.id && (
                    <tr key={`${b.id}-pay`} style={{ background: darkMode ? '#0F172A' : '#F0FDF4' }}>
                      <td colSpan={8} style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8', marginBottom: 3, fontWeight: 700, textTransform: 'uppercase' }}>Amount (₹)</div>
                            <input type="number" min="0.01" step="0.01" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                              style={{ background: darkMode ? '#1E293B' : '#fff', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 7, padding: '7px 10px', fontSize: 13, width: 120, outline: 'none' }} />
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8', marginBottom: 3, fontWeight: 700, textTransform: 'uppercase' }}>Mode</div>
                            <select value={payForm.mode} onChange={e => setPayForm(f => ({ ...f, mode: e.target.value }))}
                              style={{ background: darkMode ? '#1E293B' : '#fff', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 7, padding: '7px 10px', fontSize: 13, outline: 'none' }}>
                              {['Cash','Card','UPI','Bank Transfer','Cheque','Credit','Other'].map(m => <option key={m}>{m}</option>)}
                            </select>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: darkMode ? '#64748B' : '#94A3B8', marginBottom: 3, fontWeight: 700, textTransform: 'uppercase' }}>Date</div>
                            <StrictDateInput value={payForm.date} onChange={e => setPayForm(f => ({ ...f, date: e.target.value }))}
                              style={{ background: darkMode ? '#1E293B' : '#fff', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 7, padding: '7px 10px', fontSize: 13, outline: 'none' }} />
                          </div>
                          <button disabled={paySaving} onClick={handleRecordBillPayment}
                            style={{ padding: '7px 18px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: paySaving ? 0.7 : 1 }}>
                            {paySaving ? 'Saving…' : '✓ Record Payment'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ padding: '11px 14px', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  style={{ padding: '6px 14px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', color: darkMode ? '#F1F5F9' : '#0F172A', cursor: 'pointer' }}>◀ Prev</button>
                <span style={{ fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>Page {page} of {totalPages} — {total} bills</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  style={{ padding: '6px 14px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', color: darkMode ? '#F1F5F9' : '#0F172A', cursor: 'pointer' }}>Next ▶</button>
              </div>
            )}
          </>
        )}
      </div>

      {showBillModal && (
        <PurchaseBillModalInline
          supplier={editSupplier || null}
          suppliers={sharedSuppliers.length > 0 ? sharedSuppliers : suppliers}
          bill={editBill}
          onClose={() => { setShowBillModal(false); setEditBill(null); setEditSupplier(null); }}
          onSaved={() => {
            setShowBillModal(false);
            setEditBill(null);
            setEditSupplier(null);
            load();
            refreshSuppliers(true);
            refreshProducts(true);
          }}
        />
      )}

      {deletingBillId && (
        <DeletePasswordModal
          darkMode={darkMode}
          entityLabel="purchase bill"
          onConfirm={handleDeleteBillConfirmed}
          onCancel={() => setDeletingBillId(null)}
        />
      )}
    </>
  );
};

/* ── Purchase Bill Modal (Inline) ──────────── */
const PB_GST_RATES = [0, 5, 12, 18, 28];
const PB_GST_OPTIONS = PB_GST_RATES.flatMap(r => r === 0 ? [{ value: '0-ex', label: '0%' }] : [{ value: `${r}-in`, label: `${r}%-In` }, { value: `${r}-ex`, label: `${r}%-Ex` }]);
const pbGstSelectVal = (item) => `${parseFloat(item.gst_percent)||0}-${item.gst_inclusive ? 'in' : 'ex'}`;

const PurchaseBillModalInline = ({ supplier: supplierProp, suppliers = [], bill, onClose, onSaved }) => {
  const { darkMode, color } = useTheme();
  const { refreshProducts } = useData();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const req = { color: '#ef4444', marginLeft: 2 };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };

  const emptyItem = () => ({ key: Date.now() + Math.random(), product_id: null, product_name: '', quantity: 1, price: 0, gst_percent: 0, gst_inclusive: false, total: 0 });

  const [chosenSupplier, setChosenSupplier] = useState(supplierProp || null);
  const [suppliersLoading, setSuppliersLoading] = useState(!suppliers || suppliers.length === 0);
  const supplierInputRef = React.useRef(null);
  const modalFormRef = React.useRef(null);
  const [form, setForm] = useState({
    bill_type: bill?.bill_type || 'gst',
    bill_number: bill?.bill_number || '',
    bill_date: bill?.bill_date ? bill.bill_date.split('T')[0] : new Date().toISOString().split('T')[0],
    payment_mode: bill?.payment_mode || 'Cash',
    payment_status: bill?.payment_status || 'unpaid',
    payment_date: bill?.payment_date ? bill.payment_date.split('T')[0] : (bill?.bill_date ? bill.bill_date.split('T')[0] : new Date().toISOString().split('T')[0]),
    transport_name: bill?.transport_name || '',
    transport_amount: bill?.transport_amount ?? '',
    transport_payment_mode: bill?.transport_payment_mode || 'Cash',
    transport_payment_status: bill?.transport_payment_status || 'unpaid',
    transport_payment_date: bill?.transport_payment_date ? bill.transport_payment_date.split('T')[0] : (bill?.bill_date ? bill.bill_date.split('T')[0] : new Date().toISOString().split('T')[0]),
    transport_payment_ref: bill?.transport_payment_ref || '',
    notes: bill?.notes || '',
    discount_amount: bill?.discount_amount ?? '',
    shipping_amount: bill?.shipping_amount ?? '',
    round_off: bill?.round_off ?? '',
    place_of_supply: bill?.place_of_supply || 'Maharashtra',
    po_number: bill?.po_number || '',
    po_date: bill?.po_date ? bill.po_date.split('T')[0] : '',
    tax_method: bill?.tax_method || 'exclusive',
    shipping_tax_rate: bill?.shipping_tax_rate ?? '',
    shipping_cess_rate: bill?.shipping_cess_rate ?? '',
    credit_period: bill?.credit_period ?? '',
    due_date: bill?.due_date ? bill.due_date.split('T')[0] : '',
    eway_bill: bill?.eway_bill || '',
    status: bill?.status || 'received',
    payment_ref: bill?.payment_ref || '',
    tcs_rate: bill?.tcs_rate ?? '',
    tcs_amount: bill?.tcs_amount ?? '',
  });
  const [items, setItems] = useState(bill?.items?.length ? bill.items.map(i => ({ ...i, gst_inclusive: !!i.gst_inclusive, key: Math.random() })) : [emptyItem()]);
  const [partialPaidAmount, setPartialPaidAmount] = useState(() => {
    if (!bill) return '';
    if (bill.payment_status === 'paid') return String(parseFloat(bill.grand_total || 0));
    if (bill.payment_status === 'partial') {
      const paid = parseFloat(bill.paid_amount ?? ((parseFloat(bill.grand_total || 0) || 0) - (parseFloat(bill.balance_amount || 0) || 0)));
      return paid > 0 ? String(paid) : '';
    }
    return '';
  });
  const [saving, setSaving] = useState(false);
  const [productSearch, setProductSearch] = useState({});
  const [productResults, setProductResults] = useState({});
  const [productFocusedKey, setProductFocusedKey] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierFocused, setSupplierFocused] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [localSuppliers, setLocalSuppliers] = useState(suppliers);
  const [dirty, setDirty] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scannedPhotoUrl, setScannedPhotoUrl] = useState(bill?.scanned_photo_url || null);
  const [isMobileView, setIsMobileView] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));

  const isGST = form.bill_type === 'gst';
  const set = k => e => { setDirty(true); setForm(f => ({ ...f, [k]: e.target.value })); };
  const setVal = (k, v) => { setDirty(true); setForm(f => ({ ...f, [k]: v })); };
  const onBillDateChange = e => {
    const nextDate = e.target.value;
    setDirty(true);
    setForm(f => ({
      ...f,
      bill_date: nextDate,
      payment_date: (f.payment_date === f.bill_date || !f.payment_date) ? nextDate : f.payment_date,
      transport_payment_date: (f.transport_payment_date === f.bill_date || !f.transport_payment_date) ? nextDate : f.transport_payment_date,
    }));
  };
  const transport = parseFloat(form.transport_amount) || 0;

  useEffect(() => {
    const onResize = () => setIsMobileView(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleClose = useCallback(() => {
    if (dirty && !window.confirm('Discard purchase bill? All unsaved changes will be lost.')) return;
    onClose();
  }, [dirty, onClose]);

  // ── Handle OCR scan result ───────────────────
  const handleScanExtracted = useCallback(({ photo_url, extracted }) => {
    setShowScanModal(false);
    if (photo_url) setScannedPhotoUrl(photo_url);
    if (!extracted) return;

    // Robust helpers to clean Gemini output
    const parseNum = (v) => {
      if (v === null || v === undefined) return 0;
      const n = parseFloat(String(v).replace(/[^\d.\-]/g, ''));
      return isNaN(n) ? 0 : n;
    };
    const parseDate = (v) => {
      if (!v) return null;
      const s = String(v).trim();
      // Already YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      // DD/MM/YYYY or DD-MM-YYYY
      const m1 = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (m1) return `${m1[3]}-${m1[2].padStart(2,'0')}-${m1[1].padStart(2,'0')}`;
      // DD Mon YYYY (e.g. 14 Apr 2026)
      const m2 = s.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
      if (m2) {
        const months = {jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12'};
        const mon = months[m2[2].toLowerCase().slice(0,3)];
        if (mon) return `${m2[3]}-${mon}-${m2[1].padStart(2,'0')}`;
      }
      // fallback: try native Date parse
      const d = new Date(s);
      if (!isNaN(d.getTime())) return d.toISOString().split('T')[0];
      return null;
    };

    // Auto-fill form fields
    setDirty(true);
    const parsedDate = parseDate(extracted.bill_date);
    setForm(f => ({
      ...f,
      bill_number:      extracted.bill_number   || f.bill_number,
      bill_date:        parsedDate               || f.bill_date,
      place_of_supply:  extracted.place_of_supply || f.place_of_supply,
      transport_name:   extracted.transport_name  || f.transport_name,
      transport_amount: parseNum(extracted.transport_amount) > 0 ? String(parseNum(extracted.transport_amount)) : f.transport_amount,
      po_number:        extracted.po_number  || f.po_number,
      eway_bill:        extracted.eway_bill  || f.eway_bill,
      notes:            extracted.notes      || f.notes,
      discount_amount:  parseNum(extracted.discount_amount) > 0 ? String(parseNum(extracted.discount_amount)) : f.discount_amount,
    }));

    // Auto-fill supplier if found and not already chosen
    if (extracted.supplier_name && !chosenSupplier) {
      const matched = localSuppliers.find(s =>
        s.name.toLowerCase().includes(extracted.supplier_name.toLowerCase()) ||
        extracted.supplier_name.toLowerCase().includes(s.name.toLowerCase())
      );
      if (matched) setChosenSupplier(matched);
      else setSupplierSearch(extracted.supplier_name);
    }

    // Auto-fill items
    if (extracted.items && extracted.items.length > 0) {
      setItems(extracted.items.map(item => ({
        key: Date.now() + Math.random(),
        product_id: null,
        product_name: item.product_name || '',
        quantity: parseNum(item.quantity) || 1,
        price: parseNum(item.price),
        gst_percent: parseNum(String(item.gst_percent || '').replace('%', '')),
        gst_inclusive: !!item.gst_inclusive,
        total: parseNum(item.total),
        hsn_sac: item.hsn_sac || '',
      })));
    }
  }, [chosenSupplier, localSuppliers]);

  useEffect(() => {
    (async () => { try { const r = await productAPI.list({ limit: 100 }); setAllProducts(r.data.products || []); } catch {} })();
  }, []);

  // Load suppliers inside modal if none passed from parent (timing/lazy load)
  useEffect(() => {
    if (localSuppliers.length === 0 && !supplierProp) {
      setSuppliersLoading(true);
      supplierAPI.list({ page_size: 200 }).then(r => { setLocalSuppliers(r.data.suppliers || []); setSuppliersLoading(false); }).catch(() => { setSuppliersLoading(false); });
    } else {
      setSuppliersLoading(false);
    }
  }, [localSuppliers.length, supplierProp]);

  // Keep in sync if parent suppliers update after mount
  useEffect(() => { if (suppliers.length > 0) setLocalSuppliers(suppliers); }, [suppliers]);

  // Keyboard shortcuts for Purchase Bill modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { handleClose(); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) { e.preventDefault(); modalFormRef.current?.requestSubmit(); return; }
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); modalFormRef.current?.requestSubmit(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleClose]);

  const calcItemBase = (item) => parseFloat(item.quantity || 0) * parseFloat(item.price || 0);
  const calcItemGST = (item) => {
    if (!isGST) return 0;
    const base = calcItemBase(item);
    const gstPct = parseFloat(item.gst_percent || 0);
    if (item.gst_inclusive && gstPct > 0) return base - base / (1 + gstPct / 100);
    return base * (gstPct / 100);
  };
  const calcItemTotal = (item) => {
    if (item.gst_inclusive && isGST) return +calcItemBase(item).toFixed(2);
    return +(calcItemBase(item) + calcItemGST(item)).toFixed(2);
  };
  const calcItemTaxable = (item) => {
    if (!isGST) return calcItemBase(item);
    const base = calcItemBase(item);
    const gstPct = parseFloat(item.gst_percent || 0);
    if (item.gst_inclusive && gstPct > 0) return +(base / (1 + gstPct / 100)).toFixed(2);
    return +base.toFixed(2);
  };
  const totalBase = items.reduce((s, i) => s + calcItemBase(i), 0);
  const calcNetRate = (item) => {
    const total = calcItemTotal(item);
    const base = calcItemBase(item);
    const transportShare = totalBase > 0 ? transport * base / totalBase : 0;
    const qty = parseFloat(item.quantity) || 1;
    return +((total + transportShare) / qty).toFixed(4);
  };

  const handleGSTSelect = (key, compositeVal) => {
    const [rate, mode] = compositeVal.split('-');
    setDirty(true);
    setItems(prev => prev.map(i => i.key !== key ? i : { ...i, gst_percent: parseFloat(rate), gst_inclusive: mode === 'in' }));
  };
  const updateItem = (key, field, val) => { setDirty(true); setItems(prev => prev.map(i => i.key !== key ? i : { ...i, [field]: val })); };
  const removeItem = key => { setDirty(true); setItems(prev => prev.filter(i => i.key !== key)); };

  const subtotal = items.reduce((s, i) => s + calcItemTaxable(i), 0);
  const taxTotal = isGST ? items.reduce((s, i) => s + calcItemGST(i), 0) : 0;
  const grandTotal = items.reduce((s, i) => s + calcItemTotal(i), 0) + transport;
  const normalizedPartialPaid = Math.min(Math.max(parseFloat(partialPaidAmount || 0) || 0, 0), grandTotal);
  const settledPaidAmount = form.payment_status === 'paid'
    ? grandTotal
    : form.payment_status === 'partial'
      ? normalizedPartialPaid
      : 0;
  const settledBalanceAmount = Math.max(0, +(grandTotal - settledPaidAmount).toFixed(2));

  const searchProduct = async (key, q) => {
    setProductSearch(s => ({ ...s, [key]: q }));
    if (!q || q.length < 1) {
      setProductResults(s => ({ ...s, [key]: [] }));
      return;
    }
    try {
      const r = await productAPI.list({ search: q, limit: 15 });
      const results = r.data.products || [];
      if (results.length > 0) {
        setProductResults(s => ({ ...s, [key]: results }));
      } else {
        const filtered = allProducts.filter(p => p.name.toLowerCase().includes(q.toLowerCase()));
        setProductResults(s => ({ ...s, [key]: filtered.slice(0, 15) }));
      }
    } catch {
      const filtered = allProducts.filter(p => p.name.toLowerCase().includes(q.toLowerCase()));
      setProductResults(s => ({ ...s, [key]: filtered.slice(0, 15) }));
    }
  };
  const pickProduct = (key, p) => {
    setDirty(true);
    setItems(prev => prev.map(i => i.key !== key ? i : { ...i, product_id: p.id, product_name: p.name, price: parseFloat(p.purchase_price) || parseFloat(p.selling_price) || 0, gst_percent: parseFloat(p.gst_percent) || 0, gst_inclusive: false }));
    setProductSearch(s => ({ ...s, [key]: '' }));
    setProductResults(s => ({ ...s, [key]: [] }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!chosenSupplier) { alert('Please select a supplier'); return; }
    if (items.some(i => !i.product_name.trim())) { alert('All items must have a name'); return; }
    if (form.payment_status === 'partial' && (normalizedPartialPaid <= 0 || normalizedPartialPaid >= grandTotal)) {
      alert('For a partial bill, enter a paid amount greater than zero and less than the grand total.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        supplier_name: chosenSupplier.name, bill_type: form.bill_type, bill_number: form.bill_number,
        bill_date: form.bill_date, payment_mode: form.payment_mode, payment_status: form.payment_status,
        payment_date: form.payment_date || form.bill_date,
        transport_name: form.transport_name || null, transport_amount: transport, notes: form.notes,
        transport_payment_mode: form.transport_payment_mode || 'Cash',
        transport_payment_status: form.transport_payment_status || 'unpaid',
        transport_payment_date: form.transport_payment_date || form.bill_date,
        transport_payment_ref: form.transport_payment_ref || null,
        discount_amount: parseFloat(form.discount_amount) || 0,
        shipping_amount: parseFloat(form.shipping_amount) || 0,
        round_off: parseFloat(form.round_off) || 0,
        place_of_supply: form.place_of_supply || null,
        po_number: form.po_number || null,
        po_date: form.po_date || null,
        tax_method: form.tax_method || 'exclusive',
        shipping_tax_rate: parseFloat(form.shipping_tax_rate) || 0,
        shipping_cess_rate: parseFloat(form.shipping_cess_rate) || 0,
        credit_period: form.credit_period ? String(form.credit_period).trim() : null,
        due_date: form.due_date || null,
        eway_bill: form.eway_bill || null,
        status: form.status || 'received',
        payment_ref: form.payment_ref || null,
        tcs_rate: parseFloat(form.tcs_rate) || 0,
        tcs_amount: parseFloat(form.tcs_amount) || 0,
        cgst_amount: +(taxTotal / 2).toFixed(2),
        sgst_amount: +(taxTotal / 2).toFixed(2),
        igst_amount: 0, cess_amount: 0,
        items: items.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          quantity: Math.max(1, parseInt(i.quantity, 10) || 1),
          price: parseFloat(i.price) || 0,
          gst_percent: isGST ? (parseFloat(i.gst_percent) || 0) : 0,
          gst_inclusive: !!i.gst_inclusive,
          total: calcItemTotal(i),
          net_rate: calcNetRate(i),
          unit: i.unit || 'Pcs',
          hsn_sac: i.hsn_sac || null,
          description: i.description || null,
          discount: parseFloat(i.discount) || 0,
          cgst_rate: isGST ? (parseFloat(i.gst_percent) || 0) / 2 : 0,
          sgst_rate: isGST ? (parseFloat(i.gst_percent) || 0) / 2 : 0,
          igst_rate: 0,
          cess_rate: 0,
          is_igst: false,
          price_before_discount: parseFloat(i.price) || 0,
        })),
        subtotal: +subtotal.toFixed(2), tax_total: +taxTotal.toFixed(2), grand_total: +grandTotal.toFixed(2),
        paid_amount: +settledPaidAmount.toFixed(2), balance_amount: +settledBalanceAmount.toFixed(2),
      };
      if (bill) await supplierAPI.updateBill(bill.id, payload);
      else await supplierAPI.createBill(chosenSupplier.id, payload);
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d?.msg || d?.message).filter(Boolean).join(', ')
          : (err.response?.data?.message || err.message || 'Save failed');
      alert(msg);
    }
    setSaving(false);
  };

  const filteredSuppliers = localSuppliers.filter(s => s.name.toLowerCase().includes(supplierSearch.toLowerCase()));

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: isMobileView ? 4 : 10, overflowY: 'auto' }}>
      <div style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: isMobileView ? 12 : 18, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: isMobileView ? 8 : 12, boxSizing: 'border-box', marginTop: isMobileView ? 2 : 4, marginBottom: isMobileView ? 8 : 20 }}>
      <div style={{ background: cardBg, borderRadius: 14, border: `2px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
        <form ref={modalFormRef} onSubmit={handleSubmit}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, position: 'sticky', top: 0, background: cardBg, zIndex: 300, boxShadow: '0 2px 10px rgba(0,0,0,0.08)' }}
>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
              <h3 style={{ ...pg, fontSize: isMobileView ? 13 : 15, fontWeight: 800, margin: 0 }}>
                {bill ? '✏️ Edit Purchase Bill' : '📦 New Purchase Bill'}
                {chosenSupplier ? ` — ${chosenSupplier.name}` : ''}
              </h3>
              <div style={{ display: 'flex', background: darkMode ? '#0F172A' : '#F1F5F9', borderRadius: 8, padding: 2, gap: 2 }}>
                {[['gst', '📋 GST'], ['non_gst', '🧾 NON-GST']].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setVal('bill_type', val)}
                    style={{ padding: '5px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12,
                      background: form.bill_type === val ? acc : 'transparent',
                      color: form.bill_type === val ? '#fff' : (darkMode ? '#64748B' : '#94A3B8') }}>
                    {label}
                  </button>
                ))}
              </div>
              <button type="button" onClick={() => setShowScanModal(true)}
                title="Scan a printed invoice to auto-fill this form"
                style={{ padding: '5px 14px', borderRadius: 8, border: `1.5px solid ${acc}`, background: 'transparent', color: acc, fontWeight: 700, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: isMobileView ? 'normal' : 'nowrap' }}>
                📷 Scan Invoice
              </button>
            </div>
            <button type="button" onClick={handleClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8' }}>×</button>
          </div>
          <div style={{ padding: isMobileView ? '12px 10px' : '16px 20px' }}>
            {!supplierProp && (
              <div style={{ marginBottom: 14, padding: 12, background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10, border: `1px solid ${border}` }}>
                <label style={lbl}>Select Supplier<span style={req}>*</span></label>
                {chosenSupplier ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: acc }}>{chosenSupplier.name}</span>
                    <button type="button" onClick={() => { setDirty(true); setChosenSupplier(null); }} style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>✕ Change</button>
                  </div>
                ) : (
                  <div style={{ position: 'relative', zIndex: 110 }}>
                    <input style={inp} placeholder="Type supplier name to search…" value={supplierSearch}
                      onChange={e => { setDirty(true); setSupplierSearch(e.target.value); }}
                      ref={supplierInputRef}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') { e.preventDefault(); const items = document.querySelectorAll('[data-supplier-item]'); if (items.length > 0) items[0].focus(); }
                        if (e.key === 'Escape') { setSupplierSearch(''); }
                      }} />
                    {supplierSearch.trim().length > 0 && (
                      suppliersLoading ? (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: cardBg, border: `1px solid ${border}`, borderRadius: 8, zIndex: 20, marginTop: 2, padding: '12px', textAlign: 'center', color: darkMode ? '#94A3B8' : '#64748B', fontSize: 12 }}>Loading suppliers…</div>
                      ) : filteredSuppliers.length > 0 ? (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: cardBg, border: `1px solid ${border}`, borderRadius: 8, zIndex: 120, maxHeight: 200, overflow: 'auto', marginTop: 2, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
                          {filteredSuppliers.map(s => (
                            <div key={s.id} data-supplier-item tabIndex={0} onClick={() => { setDirty(true); setChosenSupplier(s); setSupplierSearch(''); setSupplierFocused(false); }}
                              style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 13, borderBottom: `1px solid ${border}` }}
                              onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#1E293B' : '#F1F5F9'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { setDirty(true); setChosenSupplier(s); setSupplierSearch(''); setSupplierFocused(false); }
                                if (e.key === 'ArrowUp' && e.target.previousSibling) e.target.previousSibling.focus();
                                if (e.key === 'ArrowDown' && e.target.nextSibling) e.target.nextSibling.focus();
                                if (e.key === 'Escape') { setSupplierFocused(false); setSupplierSearch(''); }
                              }}>
                              {s.name} {s.phone && <span style={{ color: darkMode ? '#64748B' : '#94A3B8', fontSize: 11 }}>· {s.phone}</span>}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: cardBg, border: `1px solid ${border}`, borderRadius: 8, zIndex: 20, marginTop: 2, padding: '12px', textAlign: 'center', color: darkMode ? '#94A3B8' : '#64748B', fontSize: 12 }}>No suppliers found</div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
              <div><label style={lbl}>Bill / Invoice No.<span style={req}>*</span></label><input style={inp} required value={form.bill_number} onChange={set('bill_number')} placeholder="Supplier's bill #" /></div>
              <div><label style={lbl}>Bill Date<span style={req}>*</span></label><StrictDateInput style={inp}  required value={form.bill_date} onChange={onBillDateChange} /></div>
              <div><label style={lbl}>Place of Supply</label><input style={inp} value={form.place_of_supply} onChange={set('place_of_supply')} placeholder="State / UT" /></div>
              <div><label style={lbl}>Tax Method</label><select style={inp} value={form.tax_method} onChange={set('tax_method')}><option value="exclusive">Exclusive</option><option value="inclusive">Inclusive</option></select></div>
              <div><label style={lbl}>Bill Status</label><select style={inp} value={form.status} onChange={set('status')}>{['received','pending','returned','cancelled'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}</select></div>
            </div>
            {/* PO & Credit Details */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14, padding: '10px 14px', background: darkMode ? '#0F172A' : '#F0F9FF', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#BAE6FD'}` }}>
              <div><label style={{ ...lbl, color: '#0284c7' }}>PO Number</label><input style={inp} value={form.po_number} onChange={set('po_number')} placeholder="Purchase order #" /></div>
              <div><label style={{ ...lbl, color: '#0284c7' }}>PO Date</label><StrictDateInput style={inp}  value={form.po_date} onChange={set('po_date')} /></div>
              <div><label style={{ ...lbl, color: '#0284c7' }}>Credit Period (Days)</label><input style={inp} type="number" min="0" value={form.credit_period} onChange={set('credit_period')} placeholder="0" /></div>
              <div><label style={{ ...lbl, color: '#0284c7' }}>Due Date</label><StrictDateInput style={inp}  value={form.due_date} onChange={set('due_date')} /></div>
              <div><label style={{ ...lbl, color: '#0284c7' }}>E-Way Bill</label><input style={inp} value={form.eway_bill} onChange={set('eway_bill')} placeholder="E-Way bill number" /></div>
            </div>
            {/* Transport & Charges */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14, padding: '10px 14px', background: darkMode ? '#0F172A' : '#FFFBEB', borderRadius: 10, border: `1px solid ${darkMode ? '#334155' : '#FDE68A'}` }}>
              <div><label style={{ ...lbl, color: '#d97706' }}>🚚 Transport / Freight</label><input style={inp} value={form.transport_name} onChange={set('transport_name')} placeholder="Carrier name" /></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Transport Amt (₹)</label><input style={inp} type="number" min="0" step="0.01" value={form.transport_amount} onChange={set('transport_amount')} placeholder="0.00" /></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Transport Pay Mode</label><select style={inp} value={form.transport_payment_mode} onChange={set('transport_payment_mode')}>{['Cash','Card','UPI','Bank Transfer','Credit','Cheque'].map(m => <option key={m}>{m}</option>)}</select></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Transport Pay Status</label><select style={inp} value={form.transport_payment_status} onChange={set('transport_payment_status')}><option value="paid">✅ Paid</option><option value="unpaid">⏳ Unpaid</option><option value="partial">🔶 Partial</option></select></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Transport Pay Date</label><StrictDateInput style={inp}  value={form.transport_payment_date} onChange={set('transport_payment_date')} /></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Transport Pay Ref</label><input style={inp} value={form.transport_payment_ref} onChange={set('transport_payment_ref')} placeholder="Txn / Ref #" /></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Shipping (₹)</label><input style={inp} type="number" min="0" step="0.01" value={form.shipping_amount} onChange={set('shipping_amount')} placeholder="0.00" /></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Discount (₹)</label><input style={inp} type="number" min="0" step="0.01" value={form.discount_amount} onChange={set('discount_amount')} placeholder="0.00" /></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>Round Off (₹)</label><input style={inp} type="number" step="0.01" value={form.round_off} onChange={set('round_off')} placeholder="0.00" /></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>TCS Rate %</label><input style={inp} type="number" min="0" step="0.01" value={form.tcs_rate} onChange={set('tcs_rate')} placeholder="0" /></div>
              <div><label style={{ ...lbl, color: '#d97706' }}>TCS Amount (₹)</label><input style={inp} type="number" min="0" step="0.01" value={form.tcs_amount} onChange={set('tcs_amount')} placeholder="0.00" /></div>
            </div>
            {/* Items */}
            <div style={{ fontWeight: 700, fontSize: 13, ...pg, marginBottom: 6 }}>📋 Items<span style={req}>*</span></div>
            <div style={{ border: `1px solid ${border}`, borderRadius: 12, marginBottom: 12, background: darkMode ? '#0B1220' : '#F8FAFC', padding: isMobileView ? '8px' : '10px', overflow: 'visible' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {items.map((item, index) => (
                  <div key={item.key} style={{ border: `1px solid ${border}`, borderRadius: 10, padding: isMobileView ? 8 : 10, background: cardBg, overflow: 'visible', position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: darkMode ? '#94A3B8' : '#64748B' }}>Item {index + 1}</div>
                      <button
                        type="button"
                        onClick={() => removeItem(item.key)}
                        disabled={items.length === 1}
                        style={{ background: 'none', border: 'none', cursor: items.length === 1 ? 'not-allowed' : 'pointer', color: '#ef4444', fontSize: 16, padding: '2px 6px' }}
                        title="Remove item"
                      >
                        ×
                      </button>
                    </div>

                    <div style={{ position: 'relative', marginBottom: 8, zIndex: productFocusedKey === item.key ? 60 : 1 }}>
                      <label style={{ ...lbl, marginBottom: 3 }}>Product / Item<span style={req}>*</span></label>
                      <input
                        style={{ ...inp, padding: '8px 10px' }}
                        value={item.product_id ? item.product_name : (productSearch[item.key] ?? item.product_name)}
                        onChange={e => {
                          updateItem(item.key, 'product_name', e.target.value);
                          updateItem(item.key, 'product_id', null);
                          searchProduct(item.key, e.target.value);
                        }}
                        onFocus={() => setProductFocusedKey(item.key)}
                        onBlur={() => setTimeout(() => {
                          setProductResults(s => ({ ...s, [item.key]: [] }));
                          setProductFocusedKey(current => (current === item.key ? null : current));
                        }, 220)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            const menuItems = document.querySelectorAll(`[data-product-item="${item.key}"]`);
                            if (menuItems.length > 0) menuItems[0].focus();
                          }
                          if (e.key === 'Escape') {
                            setProductResults(s => ({ ...s, [item.key]: [] }));
                            setProductFocusedKey(null);
                          }
                        }}
                        placeholder="Type product name to search..."
                      />

                      {productFocusedKey === item.key && productResults[item.key]?.length > 0 && (
                        <div
                          onMouseDown={e => e.preventDefault()}
                          style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: cardBg, border: `1px solid ${border}`, borderRadius: 8, zIndex: 80, maxHeight: 280, overflow: 'auto', marginTop: 3, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
                        >
                          {productResults[item.key].map(p => (
                            <div
                              key={p.id}
                              data-product-item={item.key}
                              tabIndex={0}
                              onClick={() => pickProduct(item.key, p)}
                              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: `1px solid ${border}` }}
                              onMouseEnter={e => e.currentTarget.style.background = darkMode ? '#1E293B' : '#F1F5F9'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') pickProduct(item.key, p);
                                if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  if (e.currentTarget.previousSibling) e.currentTarget.previousSibling.focus();
                                }
                                if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.focus();
                                }
                                if (e.key === 'Escape') {
                                  setProductResults(s => ({ ...s, [item.key]: [] }));
                                  setProductFocusedKey(null);
                                }
                              }}
                            >
                              <span style={{ fontWeight: 600 }}>{p.name}</span>
                              <span style={{ color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, marginLeft: 8 }}>₹{p.purchase_price || p.selling_price}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isGST ? 'repeat(auto-fit, minmax(110px, 1fr))' : 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
                      <div>
                        <label style={{ ...lbl, marginBottom: 3 }}>Qty</label>
                        <input style={{ ...inp, padding: '8px 10px' }} type="number" min="1" value={item.quantity} onChange={e => updateItem(item.key, 'quantity', e.target.value)} />
                      </div>
                      <div>
                        <label style={{ ...lbl, marginBottom: 3 }}>Price (₹)</label>
                        <input style={{ ...inp, padding: '8px 10px' }} type="number" min="0" step="0.01" value={item.price} onChange={e => updateItem(item.key, 'price', e.target.value)} />
                      </div>
                      {isGST && (
                        <div>
                          <label style={{ ...lbl, marginBottom: 3 }}>GST %</label>
                          <select style={{ ...inp, padding: '8px 10px' }} value={pbGstSelectVal(item)} onChange={e => handleGSTSelect(item.key, e.target.value)}>
                            {PB_GST_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                        <label style={{ ...lbl, marginBottom: 3 }}>Total</label>
                        <div style={{ ...inp, padding: '8px 10px', fontWeight: 800, color: acc, background: darkMode ? '#0F172A' : '#EEF6FF' }}>₹{fmtNum(calcItemTotal(item))}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button type="button" onClick={() => { setDirty(true); setItems(p => [...p, emptyItem()]); }} style={{ padding: '8px 14px', background: 'transparent', border: `1px dashed ${acc}`, color: acc, borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, marginBottom: 14, width: isMobileView ? '100%' : 'auto' }}>+ Add Item</button>
            {/* Totals */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={lbl}>Notes</label>
                <textarea style={{ ...inp, minHeight: 56, resize: 'vertical' }} value={form.notes} onChange={set('notes')} placeholder="Any notes…" />
                <div style={{ marginTop: 10, padding: '10px 12px', background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px solid ${border}`, borderRadius: 10 }}>
                  <div style={{ ...lbl, marginBottom: 6 }}>Supplier Payment Details</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
                    <div><label style={lbl}>Payment Mode</label><select style={inp} value={form.payment_mode} onChange={set('payment_mode')}>{['Cash','Card','UPI','Bank Transfer','Credit','Cheque'].map(m => <option key={m}>{m}</option>)}</select></div>
                    <div><label style={lbl}>Payment Status</label><select style={inp} value={form.payment_status} onChange={set('payment_status')}><option value="paid">✅ Paid</option><option value="unpaid">⏳ Unpaid</option><option value="partial">🔶 Partial</option></select></div>
                    <div><label style={lbl}>Payment Date</label><StrictDateInput style={inp}  value={form.payment_date} onChange={set('payment_date')} /></div>
                    <div><label style={lbl}>Payment Ref</label><input style={inp} value={form.payment_ref} onChange={set('payment_ref')} placeholder="Txn / Ref #" /></div>
                    {form.payment_status === 'partial' && (
                      <div><label style={lbl}>Paid Amount (₹)</label><input style={inp} type="number" min="0" step="0.01" value={partialPaidAmount} onChange={e => { setDirty(true); setPartialPaidAmount(e.target.value); }} placeholder="0.00" /></div>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderRadius: 10, padding: '14px 18px', border: `1px solid ${border}`, minWidth: 0, width: '100%', maxWidth: 320 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4 }}><span>Subtotal:</span><span>₹{fmtNum(subtotal)}</span></div>
                {isGST && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 4 }}><span>GST:</span><span>₹{fmtNum(taxTotal)}</span></div>}
                {transport > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#d97706', marginBottom: 4 }}><span>🚚 Transport:</span><span>₹{fmtNum(transport)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 800, color: acc, borderTop: `1px solid ${border}`, paddingTop: 8, marginTop: 4 }}><span>Grand Total:</span><span>₹{fmtNum(grandTotal)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#16a34a', marginTop: 8 }}><span>Paid:</span><span>₹{fmtNum(settledPaidAmount)}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: settledBalanceAmount > 0 ? '#dc2626' : (darkMode ? '#94A3B8' : '#64748B') }}><span>Balance:</span><span>₹{fmtNum(settledBalanceAmount)}</span></div>
              </div>
            </div>
          </div>
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${border}`, display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', position: 'sticky', bottom: 0, background: cardBg, zIndex: 300, boxShadow: '0 -2px 10px rgba(0,0,0,0.08)', flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: dirty ? '#f59e0b' : (darkMode ? '#64748B' : '#94A3B8') }}>
              {dirty ? 'Unsaved changes will ask for confirmation before closing.' : 'All amounts update inventory and supplier balances together.'}
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {scannedPhotoUrl && (
                <a href={scannedPhotoUrl} target="_blank" rel="noopener noreferrer" title="View scanned invoice photo" style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: acc, textDecoration: 'none', background: darkMode ? '#0F172A' : '#F0F9FF', border: `1px solid ${acc}40`, padding: '4px 10px', borderRadius: 7 }}>
                  <img src={scannedPhotoUrl} alt="Scanned" style={{ width: 26, height: 26, objectFit: 'cover', borderRadius: 4 }} onError={e => { e.target.style.display = 'none'; }} />
                  📷 Scanned Photo
                </a>
              )}
              <button type="button" onClick={handleClose} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>Cancel</button>
              <button type="submit" disabled={saving} style={{ padding: '9px 28px', background: saving ? '#94A3B8' : acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14 }}>{saving ? '⏳ Saving…' : bill ? '💾 Update Bill' : '💾 Save Bill'}</button>
            </div>
          </div>
        </form>
      </div>
      </div>
      {showScanModal && (
        <PurchaseBillScanModal
          darkMode={darkMode}
          color={color}
          onClose={() => setShowScanModal(false)}
          onExtracted={handleScanExtracted}
        />
      )}
    </div>
  );
};

/* ── Main Billing Page ─────────────────────── */
const Billing = () => {
  const { darkMode, color } = useTheme();
  const { suppliers: sharedSuppliers, refreshSuppliers, refreshProducts } = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingAction = useRef(searchParams.get('action'));
  const pendingSource = useRef(searchParams.get('source'));
  const [sourceRoute, setSourceRoute] = useState(
    pendingSource.current || (location.state?.from === 'dashboard' ? '/dashboard' : null)
  );
  const [pendingReturn, setPendingReturn] = useState(false);
  const [mode, setMode] = useState(searchParams.get('mode') === 'purchase' ? 'purchase' : 'sale');
  const [tab, setTab] = useState(pendingAction.current === 'create' && searchParams.get('mode') !== 'purchase' ? 'create' : 'list');
  const [purchaseTab, setPurchaseTab] = useState(pendingAction.current === 'create' && searchParams.get('mode') === 'purchase' ? 'create' : 'list');
  const [printState, setPrintState] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const acc = color.swatch;
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  useEffect(() => {
    if (searchParams.get('action') || searchParams.get('mode') || searchParams.get('source')) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('action'); next.delete('mode'); next.delete('source');
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    (async () => { try { const r = await supplierAPI.list({ page_size: 200 }); setSuppliers(r.data.suppliers || []); } catch {} })();
  }, []);

  const createDirtyRef = useRef(false);

  const closeCreateInvoice = useCallback(() => {
    if (createDirtyRef.current && !window.confirm('Discard this invoice? All unsaved changes will be lost.')) {
      return;
    }
    createDirtyRef.current = false;
    setEditingInvoice(null);
    if (sourceRoute) {
      setPendingReturn(true);
      setTab('list');
      return;
    }
    setTab('list');
  }, [sourceRoute]);

  // Global keyboard shortcuts for Billing page
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT' || e.target.isContentEditable)) return;

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        if (mode === 'sale') {
          setTab('create');
        } else {
          setPurchaseTab('create');
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        setMode(prev => prev === 'sale' ? 'purchase' : 'sale');
      }

      if (e.key === 'Escape') {
        if (mode === 'sale' && tab === 'create') {
          closeCreateInvoice();
          return;
        }
        if (mode === 'sale' && tab === 'edit') {
          if (window.confirm('Discard changes to this invoice?')) { setEditingInvoice(null); setTab('list'); }
          return;
        }
        if (mode === 'purchase' && purchaseTab === 'create') {
          setPurchaseTab('list');
          return;
        }
        // On list tab, ESC returns to source page if opened via shortcut
        if (sourceRoute && ((mode === 'sale' && tab === 'list') || (mode === 'purchase' && purchaseTab === 'list'))) {
          const dest = sourceRoute;
          setSourceRoute(null);
          navigate(dest, { replace: true });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [mode, tab, purchaseTab, closeCreateInvoice, sourceRoute, navigate]);

  // Listen for sv-open-modal custom event (e.g. from sidebar/dashboard)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail === 'new-invoice') {
        setMode('sale');
        setTab('create');
      }
      if (e.detail === 'purchase-bill') {
        setMode('purchase');
        setPurchaseTab('create');
      }
    };
    window.addEventListener('sv-open-modal', handler);
    return () => window.removeEventListener('sv-open-modal', handler);
  }, []);

  const openPrint = async (invoiceOrId) => {
    try {
      const id = typeof invoiceOrId === 'object' && invoiceOrId !== null ? invoiceOrId.id : invoiceOrId;
      const [invRes, shopRes] = await Promise.all([invoiceAPI.get(id), settingsAPI.get()]);
      setPrintState({ invoice: invRes.data, shop: shopRes.data, format: shopRes.data.print_format || 'classic' });
    } catch { alert('Failed to load invoice for printing'); }
  };

  const openEdit = async (invOrId) => {
    try {
      const id = typeof invOrId === 'object' && invOrId !== null ? invOrId.id : invOrId;
      const res = await invoiceAPI.get(id);
      setEditingInvoice(res.data);
      setTab('edit');
    } catch { alert('Failed to load invoice details'); }
  };

  const handleCreateSuccess = (id) => {
    const fromSource = sourceRoute;
    setSourceRoute(null);
    setTab('list');
    if (id) setTimeout(() => openPrint(id), 500);
    if (fromSource) setTimeout(() => navigate(fromSource, { replace: true }), 600);
  };

  useEffect(() => {
    if (pendingReturn && tab === 'list' && sourceRoute) {
      setPendingReturn(false);
      const dest = sourceRoute;
      setSourceRoute(null);
      navigate(dest, { replace: true });
    }
  }, [pendingReturn, tab, navigate, sourceRoute]);

  return (
    <Layout>
      <div className="page-enter" style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ ...pg, fontSize: 26, fontWeight: 800, margin: 0 }}>Billing</h1>
          <p style={{ ...muted, margin: '3px 0 0', fontSize: 13 }}>Create and manage sale invoices & purchase bills</p>
        </div>

        {/* ── Mode Toggle: Sale Invoice / Purchase Bill ── */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <button onClick={() => { setMode('sale'); setTab('list'); }}
            style={{
              flex: 1, minWidth: 200, maxWidth: 340, padding: '18px 24px',
              borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s',
              background: mode === 'sale'
                ? (darkMode ? 'linear-gradient(135deg, #1e3a5f, #1E293B)' : 'linear-gradient(135deg, #eff6ff, #dbeafe)')
                : (darkMode ? '#1E293B' : '#fff'),
              border: `2.5px solid ${mode === 'sale' ? '#3B82F6' : (darkMode ? '#334155' : '#E2E8F0')}`,
              boxShadow: mode === 'sale' ? '0 4px 20px rgba(59,130,246,0.15)' : 'none',
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: mode === 'sale' ? '#3B82F6' : (darkMode ? '#334155' : '#E2E8F0'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>🧾</div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: mode === 'sale' ? '#3B82F6' : (darkMode ? '#94A3B8' : '#64748B') }}>Sale Invoice</div>
              <div style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8', marginTop: 2 }}>Create & manage sales bills</div>
            </div>
          </button>
          <button onClick={() => { setMode('purchase'); setPurchaseTab('list'); }}
            style={{
              flex: 1, minWidth: 200, maxWidth: 340, padding: '18px 24px',
              borderRadius: 14, cursor: 'pointer', transition: 'all 0.2s',
              background: mode === 'purchase'
                ? (darkMode ? 'linear-gradient(135deg, #1a2e1a, #1E293B)' : 'linear-gradient(135deg, #f0fdf4, #dcfce7)')
                : (darkMode ? '#1E293B' : '#fff'),
              border: `2.5px solid ${mode === 'purchase' ? '#16A34A' : (darkMode ? '#334155' : '#E2E8F0')}`,
              boxShadow: mode === 'purchase' ? '0 4px 20px rgba(22,163,74,0.15)' : 'none',
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: mode === 'purchase' ? '#16A34A' : (darkMode ? '#334155' : '#E2E8F0'), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>📦</div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: 17, fontWeight: 800, color: mode === 'purchase' ? '#16A34A' : (darkMode ? '#94A3B8' : '#64748B') }}>Purchase Bill</div>
              <div style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8', marginTop: 2 }}>Record supplier purchases</div>
            </div>
          </button>
        </div>

        {/* ── Sale Invoice Section ── */}
        {mode === 'sale' && (
          <>
            <div style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`, marginBottom: 22, display: 'flex', gap: 4 }}>
              {[['list', '📋 Invoice List'], ['create', '➕ Create Invoice'], ...(tab === 'edit' ? [['edit', '✏️ Edit Invoice']] : [])].map(([id, label]) => (
                <button key={id} onClick={() => { if (id !== 'edit') setEditingInvoice(null); setTab(id); }} style={{
                  padding: '9px 22px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                  background: 'transparent', borderBottom: tab === id ? `3px solid ${acc}` : '3px solid transparent',
                  color: tab === id ? acc : (darkMode ? '#64748B' : '#94A3B8'), marginBottom: -2, transition: 'all 0.2s',
                }}>{label}</button>
              ))}
            </div>
            <InvoiceList onCreateNew={() => setTab('create')} onPrint={openPrint} onEdit={openEdit} darkMode={darkMode} color={color} />
            {(tab === 'create' || (tab === 'edit' && editingInvoice)) && (
              <>
                <style>{`@keyframes billPopIn{from{opacity:0;transform:translateY(32px) scale(0.97)}to{opacity:1;transform:none}}`}</style>
                <div onClick={e => { if (e.target === e.currentTarget) closeCreateInvoice(); }} style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '10px', overflowY: 'auto' }}>
                  <div style={{ animation: 'billPopIn 0.22s cubic-bezier(.22,.68,0,1.2)', width: '100%', display: 'flex', justifyContent: 'center', paddingTop: 4 }}>
                    {tab === 'create' && <CreateInvoice onSuccess={handleCreateSuccess} onClose={closeCreateInvoice} darkMode={darkMode} color={color} dirtyRef={createDirtyRef} />}
                    {tab === 'edit' && editingInvoice && <CreateInvoice key={editingInvoice.id} editInvoice={editingInvoice} onSuccess={() => { setEditingInvoice(null); setTab('list'); }} onClose={() => { if (window.confirm('Discard changes to this invoice?')) { setEditingInvoice(null); setTab('list'); } }} darkMode={darkMode} color={color} />}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* ── Purchase Bill Section ── */}
        {mode === 'purchase' && (
          <>
            <div style={{ borderBottom: `2px solid ${darkMode ? '#334155' : '#E2E8F0'}`, marginBottom: 22, display: 'flex', gap: 4 }}>
              {[['list', '📋 Purchase List'], ['create', '➕ Create Purchase']].map(([id, label]) => (
                <button key={id} onClick={() => setPurchaseTab(id)} style={{
                  padding: '9px 22px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13,
                  background: 'transparent', borderBottom: purchaseTab === id ? `3px solid #16A34A` : '3px solid transparent',
                  color: purchaseTab === id ? '#16A34A' : (darkMode ? '#64748B' : '#94A3B8'), marginBottom: -2, transition: 'all 0.2s',
                }}>{label}</button>
              ))}
            </div>
            {purchaseTab === 'list' && (
              <PurchaseBillList onCreateNew={() => setPurchaseTab('create')} darkMode={darkMode} color={color} />
            )}
            {purchaseTab === 'create' && (
              <PurchaseBillModalInline
                supplier={null}
                suppliers={sharedSuppliers.length > 0 ? sharedSuppliers : suppliers}
                bill={null}
                onClose={() => setPurchaseTab('list')}
                onSaved={() => {
                  setPurchaseTab('list');
                  refreshSuppliers(true);
                  refreshProducts(true);
                }}
              />
            )}
          </>
        )}
      </div>
      {printState && (
        <PrintModal invoice={printState.invoice} shop={printState.shop} format={printState.format}
          onFormatChange={f => setPrintState(p => ({ ...p, format: f }))}
          onClose={() => setPrintState(null)} />
      )}
    </Layout>
  );
};

/* ── Delete Password Modal ─────────────────── */
const DeletePasswordModal = ({ onConfirm, onCancel, darkMode, entityLabel = 'invoice' }) => {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 80); }, []);
  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  const submit = async () => {
    if (!pw.trim()) { setErr('Enter your account password'); return; }
    setBusy(true); setErr('');
    try {
      await authAPI.verifyPassword(pw);
      onConfirm();
    } catch (e) {
      if (mountedRef.current) setErr(e.response?.data?.detail || 'Incorrect password');
    }
    if (mountedRef.current) setBusy(false);
  };

  const bg = darkMode ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.5)';
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 14 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: bg, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ ...card, padding: 28, maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', width: '100%' }}>
        <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 8 }}>🔐</div>
        <h3 style={{ color: darkMode ? '#F1F5F9' : '#0F172A', textAlign: 'center', margin: '0 0 6px', fontSize: 17, fontWeight: 800 }}>Confirm Delete</h3>
        <p style={{ color: darkMode ? '#64748B' : '#94A3B8', textAlign: 'center', fontSize: 13, margin: '0 0 20px', lineHeight: 1.5 }}>
          This will <strong style={{ color: '#dc2626' }}>permanently delete</strong> this {entityLabel}.<br/>Enter your account password to confirm.
        </p>
        <input ref={inputRef} type="password" style={inp} placeholder="Your account password"
          value={pw} onChange={e => { setPw(e.target.value); setErr(''); }}
          onKeyDown={e => e.key === 'Enter' && submit()} />
        {err && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6, fontWeight: 600 }}>⚠ {err}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={onCancel} style={{ flex: 1, padding: '10px', background: 'transparent', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, color: darkMode ? '#94A3B8' : '#64748B', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
          <button onClick={submit} disabled={busy} style={{ flex: 1, padding: '10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer' }}>
            {busy ? '⏳ Verifying…' : '🗑️ Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Invoice List ──────────────────────────── */
const InvoiceList = ({ onCreateNew, onPrint, onEdit, darkMode, color }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const searchRef = useRef(null);
  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };

  const selectedInv = invoices.find(i => i.id === selectedId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await invoiceAPI.list({ page, limit: 15, search: search || undefined, payment_status: statusFilter || undefined, exclude_bill_types: 'quotation,proforma' });
      setInvoices(res.data.invoices || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }, [page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { invoiceAPI.stats().then(r => setStats(r.data)).catch(() => {}); }, []);

  // Keyboard shortcuts for list
  useEffect(() => {
    const handler = e => {
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      // Arrow key navigation through rows
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const idx = invoices.findIndex(i => i.id === selectedId);
        if (idx === -1 && invoices.length > 0) setSelectedId(invoices[0].id);
        else if (idx < invoices.length - 1) setSelectedId(invoices[idx + 1].id);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const idx = invoices.findIndex(i => i.id === selectedId);
        if (idx > 0) setSelectedId(invoices[idx - 1].id);
        return;
      }
      if (e.key === 'n' || e.key === 'N') { e.preventDefault(); onCreateNew(); }
      if (e.key === '/' || e.key === 'f') { e.preventDefault(); searchRef.current?.focus(); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && !e.ctrlKey) {
        e.preventDefault(); setDeletingId(selectedId);
      }
      if (e.key === 'Enter' || e.key === 'e' || e.key === 'E') { if (selectedInv) onEdit(selectedInv); }
      if (e.key === 'p' || e.key === 'P') { if (selectedId) onPrint(selectedId); }
      if (e.key === 'Escape') setSelectedId(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedId, selectedInv, invoices, onCreateNew, onEdit, onPrint]);

  const handleDeleteConfirmed = async () => {
    const id = deletingId;
    setDeletingId(null);
    try { await invoiceAPI.delete(id); setSelectedId(null); load(); invoiceAPI.stats().then(r => setStats(r.data)).catch(() => {}); }
    catch { alert('Delete failed'); }
  };

  const statusBadge = s => {
    const map = { paid: '#dcfce7:#166534', unpaid: '#fee2e2:#991b1b', partial: '#fef9c3:#854d0e' };
    const [bg, tx] = (map[s] || '#f1f5f9:#374151').split(':');
    return <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: bg, color: tx }}>{s}</span>;
  };
  const typeBadge = t => {
    const labels = { tax_invoice: 'GST', non_gst: 'Cash', quotation: 'Quote', bill_of_supply: 'BoS', proforma: 'Pro-F' };
    return <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700, background: darkMode ? '#1e3a5f' : '#dbeafe', color: darkMode ? '#93c5fd' : '#1d4ed8' }}>{labels[t] || t}</span>;
  };
  const totalPages = Math.ceil(total / 15);

  return (
    <>
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14, marginBottom: 18 }}>
          {[
            ['Total Invoices', stats.total_invoices, darkMode ? '#F1F5F9' : '#0F172A'],
            ['Revenue', `₹${fmtNum(stats.total_revenue)}`, '#16a34a'],
            ['Pending', `₹${fmtNum(stats.pending_amount)}`, '#dc2626'],
            ['This Month', stats.invoices_this_month, '#3b82f6'],
          ].map(([label, val, clr]) => (
            <div key={label} style={{ ...card, padding: '14px 16px' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: clr }}>{val}</div>
              <div style={{ fontSize: 12, color: darkMode ? '#64748B' : '#94A3B8', marginTop: 2 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search / Filter bar */}
      <div style={{ ...card, padding: 14, marginBottom: 14, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input ref={searchRef} style={{ ...inp, flex: 1, minWidth: 220 }} placeholder="/ Invoice#, customer, product, address, date, GSTIN, mode…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} />
        <select style={inp} value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          <option value="paid">✓ Paid</option>
          <option value="unpaid">⏳ Unpaid</option>
          <option value="partial">🔶 Partial</option>
        </select>
        <button onClick={() => navigate('/quotations')} style={{ padding: '8px 16px', background: darkMode ? '#1E293B' : '#f0f9ff', color: '#0284c7', border: `1px solid ${darkMode ? '#0284c7' : '#bae6fd'}`, borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
          📋 Quotations
        </button>
        <button onClick={onCreateNew} style={{ padding: '8px 18px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
          ➕ New <span style={{ fontWeight: 400, fontSize: 11, opacity: 0.8 }}>(N)</span>
        </button>
      </div>

      {/* Keyboard hint */}
      <div style={{ fontSize: 11, color: darkMode ? '#475569' : '#94A3B8', marginBottom: 8, paddingLeft: 4 }}>
        💡 <strong>↑↓ navigate</strong> rows · <kbd style={{ background: darkMode ? '#1E293B' : '#F1F5F9', padding: '1px 5px', borderRadius: 4, border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>Enter</kbd>/<kbd style={{ background: darkMode ? '#1E293B' : '#F1F5F9', padding: '1px 5px', borderRadius: 4, border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>E</kbd> Edit &nbsp;
        <kbd style={{ background: darkMode ? '#1E293B' : '#F1F5F9', padding: '1px 5px', borderRadius: 4, border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>P</kbd> Print &nbsp;
        <kbd style={{ background: darkMode ? '#1E293B' : '#F1F5F9', padding: '1px 5px', borderRadius: 4, border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>Del</kbd> Delete &nbsp;
        <kbd style={{ background: darkMode ? '#1E293B' : '#F1F5F9', padding: '1px 5px', borderRadius: 4, border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>/</kbd> Search &nbsp;
        <kbd style={{ background: darkMode ? '#1E293B' : '#F1F5F9', padding: '1px 5px', borderRadius: 4, border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>N</kbd> New
      </div>

      <div style={{ ...card, overflow: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 50, color: darkMode ? '#64748B' : '#94A3B8' }}>Loading...</div>
        ) : invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 52, marginBottom: 10 }}>🧾</div>
            <p style={{ color: darkMode ? '#64748B' : '#94A3B8', marginBottom: 14 }}>No invoices found</p>
            <button onClick={onCreateNew} style={{ padding: '9px 22px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Create First Invoice</button>
          </div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                  {['Invoice #', 'Type', 'Customer', 'Date', 'Amount', 'Payment', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: h === '' ? 'right' : 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map(inv => {
                  const isSelected = inv.id === selectedId;
                  return (
                    <tr key={inv.id}
                      onClick={() => setSelectedId(isSelected ? null : inv.id)}
                      style={{
                        borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`,
                        cursor: 'pointer',
                        background: isSelected ? (darkMode ? '#1e3a5f' : '#eff6ff') : 'transparent',
                        outline: isSelected ? `2px solid ${acc}` : 'none',
                        outlineOffset: -1,
                        transition: 'background 0.12s',
                      }}
                    >
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: acc, whiteSpace: 'nowrap' }}>{inv.invoice_number}</td>
                      <td style={{ padding: '10px 12px' }}>{typeBadge(inv.bill_type)}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13, color: darkMode ? '#F1F5F9' : '#0F172A' }}>{inv.customer_name}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B', whiteSpace: 'nowrap' }}>{fmtDate(inv.invoice_date || inv.created_at)}</td>
                      <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: darkMode ? '#F1F5F9' : '#0F172A', whiteSpace: 'nowrap' }}>₹{fmtNum(inv.grand_total)}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>{inv.payment_mode}</td>
                      <td style={{ padding: '10px 12px' }}>{statusBadge(inv.payment_status)}</td>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', textAlign: 'right', minWidth: isSelected ? 140 : 20 }}>
                        {isSelected && (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', alignItems: 'center' }}>
                            <button title="Edit (E)" onClick={e => { e.stopPropagation(); onEdit(inv); }}
                              style={{ padding: '5px 10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>✏️ Edit</button>
                            <button title="Print (P)" onClick={e => { e.stopPropagation(); onPrint(inv.id); }}
                              style={{ padding: '5px 10px', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>🖨️ Print</button>
                            <button title="Delete (Del)" onClick={e => { e.stopPropagation(); setDeletingId(inv.id); }}
                              style={{ padding: '5px 10px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>🗑️</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ padding: '11px 14px', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  style={{ padding: '6px 14px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', color: darkMode ? '#F1F5F9' : '#0F172A', cursor: 'pointer' }}>◀ Prev</button>
                <span style={{ fontSize: 12, color: darkMode ? '#94A3B8' : '#64748B' }}>Page {page} of {totalPages} — {total} invoices</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  style={{ padding: '6px 14px', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 6, background: 'transparent', color: darkMode ? '#F1F5F9' : '#0F172A', cursor: 'pointer' }}>Next ▶</button>
              </div>
            )}
          </>
        )}
      </div>

      {deletingId && (
        <DeletePasswordModal
          darkMode={darkMode}
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setDeletingId(null)}
        />
      )}
    </>
  );
};

/* ── Create Invoice ────────────────────────── */
const CreateInvoice = ({ onSuccess, onClose, darkMode, color, editInvoice = null, dirtyRef }) => {
  const isEdit = !!editInvoice;
  const { refreshCustomers, refreshProducts } = useData();
  const acc = color.swatch;
  const BG = darkMode ? '#0F172A' : '#F1F5F9';
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 7, padding: '8px 10px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const req = { color: '#ef4444', marginLeft: 2 };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  const [mobile, setMobile] = useState(window.innerWidth < 820);
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < 820);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const pairCols = mobile ? '1fr' : '1fr 1fr';
  const modalMaxWidth = 'min(1120px, calc(100vw - 12px))';

  const [billType, setBillType] = useState(editInvoice?.bill_type || 'tax_invoice');
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [walkinName, setWalkinName] = useState(editInvoice ? (editInvoice.customer_name || '') : '');
  const [walkinPhone, setWalkinPhone] = useState(editInvoice ? (editInvoice.customer_phone || '') : '');
  const [walkinAddress, setWalkinAddress] = useState(editInvoice ? (editInvoice.customer_address || '') : '');
  const [walkinGstin, setWalkinGstin] = useState(editInvoice ? (editInvoice.customer_gstin || '') : '');
  const [showQuickCust, setShowQuickCust] = useState(false);
  const [quickCustName, setQuickCustName] = useState('');
  const [quickCustPhone, setQuickCustPhone] = useState('');
  const [quickCustAddress, setQuickCustAddress] = useState('');
  const [quickCustSaving, setQuickCustSaving] = useState(false);
  const [cart, setCart] = useState(() => {
    if (editInvoice?.items) {
      const isGSTInit = editInvoice.bill_type === 'tax_invoice' || editInvoice.bill_type === 'bill_of_supply';
      return editInvoice.items.map(i => {
        // Extract serial numbers from description if present (supports S/N: sn1 / sn2 format)
        let desc = i.description || '';
        let sn1 = '';
        let sn2 = '';
        const snMatch = desc.match(/S\/N:\s*(.+?)(?:\s*\||$)/);
        if (snMatch) {
          const snRaw = snMatch[1].trim();
          const parts = snRaw.split(/\s*\/\s*/);
          sn1 = (parts[0] || '').trim();
          sn2 = (parts[1] || '').trim();
          desc = desc.replace(/\s*\|?\s*S\/N:\s*.+$/, '').replace(/^\s*\|\s*/, '').trim();
        }
        return calcItem({ ...i, gst_inclusive: !!i.gst_inclusive, key: Date.now() + Math.random(), description: desc, serial_number_1: sn1, serial_number_2: sn2 }, isGSTInit);
      });
    }
    return [];
  });
  const [paymentMode, setPaymentMode] = useState(editInvoice?.payment_mode || 'Cash');
  const [paymentStatus, setPaymentStatus] = useState(editInvoice?.payment_status || 'paid');
  const [notes, setNotes] = useState(editInvoice?.notes || '');
  const [saving, setSaving] = useState(false);
  // BillSoft additional invoice fields
  const [invoiceDate, setInvoiceDate] = useState(editInvoice?.invoice_date ? editInvoice.invoice_date.split('T')[0] : new Date().toISOString().split('T')[0]);
  const [customerState, setCustomerState] = useState(editInvoice?.customer_state || 'Maharashtra');
  const [deliveryAddress, setDeliveryAddress] = useState(editInvoice?.customer_address || '');
  const [discountAmount, setDiscountAmount] = useState(editInvoice?.discount_amount ?? '');
  const [shippingAmount, setShippingAmount] = useState(editInvoice?.shipping_amount ?? '');
  const [roundOff, setRoundOff] = useState(editInvoice?.round_off ?? '');
  const [soldBy, setSoldBy] = useState(editInvoice?.sold_by || '');
  const [docReference, setDocReference] = useState(editInvoice?.doc_reference || '');
  const [docReferenceNo, setDocReferenceNo] = useState(editInvoice?.doc_reference_no || '');
  const [taxMethod, setTaxMethod] = useState(editInvoice?.tax_method || 'exclusive');
  const [shippingTaxRate, setShippingTaxRate] = useState(editInvoice?.shipping_tax_rate ?? '');
  const [shippingCessRate, setShippingCessRate] = useState(editInvoice?.shipping_cess_rate ?? '');
  const [invoiceStatus, setInvoiceStatus] = useState(editInvoice?.status || 'active');
  const [dueDate, setDueDate] = useState(editInvoice?.due_date ? editInvoice.due_date.split('T')[0] : '');
  const [tcsRate, setTcsRate] = useState(editInvoice?.tcs_rate ?? '');
  const [tcsAmount, setTcsAmount] = useState(editInvoice?.tcs_amount ?? '');
  const [poNumber, setPoNumber] = useState(editInvoice?.po_number || '');
  const [poDate, setPoDate] = useState(editInvoice?.po_date ? editInvoice.po_date.split('T')[0] : '');
  const [deliveryTerms, setDeliveryTerms] = useState(editInvoice?.delivery_terms || '');
  const [transportMode, setTransportMode] = useState(editInvoice?.transport_mode || '');
  const [vehicleNumber, setVehicleNumber] = useState(editInvoice?.vehicle_number || '');
  const [transporterName, setTransporterName] = useState(editInvoice?.transporter_name || '');
  const [transporterId, setTransporterId] = useState(editInvoice?.transporter_id || '');
  const [transportDistance, setTransportDistance] = useState(editInvoice?.transport_distance ?? '');
  const [ewayBillNumber, setEwayBillNumber] = useState(editInvoice?.eway_bill_number || '');
  const [showMoreInvoiceDetails, setShowMoreInvoiceDetails] = useState(false);
  // Cheque fields — pre-populate from editInvoice if present
  const [chequeBank, setChequeBank] = useState(() => {
    if (!editInvoice) return '';
    const cr = editInvoice.payment_entries?.find(e => e.mode === 'Cheque');
    return cr?.cheque_bank || editInvoice.cheque_bank || '';
  });
  const [chequeNumber, setChequeNumber] = useState(() => {
    if (!editInvoice) return '';
    const cr = editInvoice.payment_entries?.find(e => e.mode === 'Cheque');
    return cr?.cheque_number || editInvoice.cheque_number || '';
  });
  const [chequeDate, setChequeDate] = useState(() => {
    if (!editInvoice) return '';
    const cr = editInvoice.payment_entries?.find(e => e.mode === 'Cheque');
    const d = cr?.cheque_date || editInvoice.cheque_date || '';
    return d ? String(d).slice(0, 10) : '';
  });
  // Finance fields — pre-populate from editInvoice if present
  const _finRec = editInvoice?.payment_entries?.find(e => e.mode === 'Finance');
  const [financeCompany, setFinanceCompany] = useState(_finRec?.finance_company || editInvoice?.finance_company || '');
  const [financeAmount, setFinanceAmount] = useState(() => { const v = _finRec?.finance_amount ?? editInvoice?.finance_amount; return v != null ? String(v) : ''; });
  const [financeScheme, setFinanceScheme] = useState(_finRec?.finance_scheme || editInvoice?.finance_scheme || '');
  const [financeProduct, setFinanceProduct] = useState(_finRec?.finance_product || editInvoice?.finance_product || '');
  const [financeEmiDate, setFinanceEmiDate] = useState(() => { const d = _finRec?.finance_emi_date || editInvoice?.finance_emi_date; return d ? String(d).slice(0, 10) : ''; });
  const [financeEmiAmount, setFinanceEmiAmount] = useState(() => { const v = _finRec?.finance_emi_amount ?? editInvoice?.finance_emi_amount; return v != null ? String(v) : ''; });
  const [financeProcessingFee, setFinanceProcessingFee] = useState(() => { const v = _finRec?.finance_processing_fee ?? editInvoice?.finance_processing_fee; return v != null ? String(v) : ''; });
  const [financeEmiTenure, setFinanceEmiTenure] = useState(() => { const v = _finRec?.finance_emi_tenure ?? editInvoice?.finance_emi_tenure; return v != null ? String(v) : ''; });
  // Multi-payment entries
  const [paymentEntries, setPaymentEntries] = useState(() => {
    if (editInvoice?.payment_entries && editInvoice.payment_entries.length > 0) {
      return editInvoice.payment_entries.map(e => ({ mode: e.mode || 'Cash', amount: String(e.amount || ''), date: e.date || e.payment_date || new Date().toISOString().split('T')[0] }));
    }
    if (editInvoice?.payment_mode) {
      return [{ mode: editInvoice.payment_mode, amount: editInvoice.paid_amount ? String(editInvoice.paid_amount) : '', date: new Date().toISOString().split('T')[0] }];
    }
    return [{ mode: 'Cash', amount: '', date: new Date().toISOString().split('T')[0] }];
  });
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showCreditConfirm, setShowCreditConfirm] = useState(false);
  // Out-of-stock notification
  const [oosNotification, setOosNotification] = useState(null);
  const [oosLoading, setOosLoading] = useState(false);
  const [showCustDD, setShowCustDD] = useState(false);
  const [showProdDD, setShowProdDD] = useState(false);
  const prodRef = useRef(null);
  const cameraRef = useRef(null);
  const [showOCRScanner, setShowOCRScanner] = useState(false);

  // Photo attachments
  const [photos, setPhotos] = useState(() => {
    if (editInvoice?.photos) {
      try {
        const parsed = typeof editInvoice.photos === 'string' ? JSON.parse(editInvoice.photos) : editInvoice.photos;
        return (parsed || []).map(url => ({ url, existing: true }));
      } catch { return []; }
    }
    return [];
  });
  const [photoLightbox, setPhotoLightbox] = useState(null);

  const isGST = billType === 'tax_invoice' || billType === 'bill_of_supply';

  // Track dirty state for parent confirm dialog
  useEffect(() => {
    if (!dirtyRef) return;
    const hasData = cart.length > 0 || !!selectedCustomer || walkinName.trim() !== '' || notes.trim() !== '';
    dirtyRef.current = isEdit || hasData;
  }, [dirtyRef, cart, selectedCustomer, walkinName, notes, isEdit]);

  useEffect(() => {
    const isPhone = /^\d+$/.test(customerSearch.trim());
    const minLen = isPhone ? 3 : 2;
    if (customerSearch.length >= minLen) {
      customerAPI.list({ search: customerSearch, limit: 8 }).then(r => { setCustomers(r.data.customers || []); setShowCustDD(true); }).catch(() => {});
    } else setShowCustDD(false);
  }, [customerSearch]);

  useEffect(() => {
    if (productSearch.length >= 1) {
      productAPI.list({ search: productSearch, limit: 10 }).then(r => { setProducts(r.data.products || []); setShowProdDD(true); }).catch(() => {});
    } else setShowProdDD(false);
  }, [productSearch]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = e => { if (prodRef.current && !prodRef.current.contains(e.target)) setShowProdDD(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addToCart = (p) => {
    const price = parseFloat(p.selling_price) || 0;
    const gst = parseFloat(p.gst_percent) || 0;
    const existing = cart.find(i => i.product_id === p.id);
    if (existing) {
      updateItem(existing.key, 'quantity', existing.quantity + 1);
    } else {
      setCart(prev => [...prev, calcItem({
        key: Date.now(),
        product_id: p.id,
        product_name: p.name,
        description: '',
        hsn_sac: p.hsn_sac || '',
        unit: p.unit || 'Pcs',
        quantity: 1,
        price,
        discount_percent: 0,
        gst_percent: gst || 18,
        gst_inclusive: true,
        mrp: parseFloat(p.mrp) || '',
        warranty: '',
        warranty_type: '',
        warranty_expiry: '',
        expiry_date: '',
        cess_rate: 0,
        alt_unit: '',
        alt_qty: '',
      }, isGST)]);
    }
    setProductSearch(''); setShowProdDD(false);
  };

  const addManualItem = () => {
    // Manual items only available in Quotations page
  };

  const updateItem = (key, field, rawValue) => {
    setCart(prev => prev.map(item => {
      if (item.key !== key) return item;
      const updated = { ...item, [field]: rawValue };
      return calcItem(updated, isGST);
    }));
  };

  const removeItem = key => setCart(prev => prev.filter(i => i.key !== key));

  // GST dropdown handler: parses "5-in" / "5-ex" and updates both gst_percent + gst_inclusive
  const handleGSTSelect = (key, compositeVal) => {
    const [rate, mode] = compositeVal.split('-');
    setCart(prev => prev.map(item => {
      if (item.key !== key) return item;
      return calcItem({ ...item, gst_percent: parseFloat(rate), gst_inclusive: mode === 'in' }, isGST);
    }));
  };

  // Recalculate all when isGST changes
  useEffect(() => { setCart(prev => prev.map(i => calcItem(i, isGST))); }, [isGST]);

  // Totals (use per-item computed values to handle inclusive/exclusive correctly)
  const grossTotal = cart.reduce((s, i) => s + n(i.price) * n(i.quantity), 0);
  const totalDiscount = cart.reduce((s, i) => s + (n(i._discAmt) || 0), 0);
  const taxableAmt = cart.reduce((s, i) => s + (n(i._taxable) || 0), 0);
  const gstTotal = isGST ? cart.reduce((s, i) => s + (n(i._gstAmt) || 0), 0) : 0;
  const grandTotal = cart.reduce((s, i) => s + (n(i.total) || 0), 0);
  const cgst = gstTotal / 2;
  const sgst = gstTotal / 2;

  // Compress image to reduce upload size while keeping good quality
  const compressImage = (file, maxWidth = 2048, quality = 0.85) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth; }
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob(blob => {
          if (blob) {
            resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
          } else {
            resolve(file);
          }
        }, 'image/jpeg', quality);
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => { URL.revokeObjectURL(img.src); resolve(file); };
      img.src = URL.createObjectURL(file);
    });
  };

  // Photo handlers
  const handlePhotoFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const remaining = 5 - photos.length;
    const files = Array.from(fileList).slice(0, remaining);
    const compressed = await Promise.all(files.map(f => compressImage(f)));
    const newPhotos = compressed.map(f => ({ url: URL.createObjectURL(f), file: f, existing: false }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const openCamera = () => {
    if (cameraRef.current) cameraRef.current.click();
  };

  const removePhoto = (idx) => {
    setPhotos(prev => {
      const p = prev[idx];
      if (p?.url && !p.existing) URL.revokeObjectURL(p.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // Revoke blob URLs on unmount to prevent memory leaks
  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(() => {
    return () => {
      photosRef.current.forEach(p => {
        if (p?.url && !p.existing) URL.revokeObjectURL(p.url);
      });
    };
  }, []);

  const uploadPhotosForInvoice = async (invoiceId) => {
    const newPhotos = photos.filter(p => !p.existing && p.file);
    let failCount = 0;
    for (const photo of newPhotos) {
      try {
        const fd = new FormData();
        fd.append('file', photo.file);
        await invoiceAPI.uploadPhoto(invoiceId, fd);
      } catch (err) {
        console.error('Photo upload failed:', err);
        failCount++;
      }
    }
    if (failCount > 0) {
      alert(`${failCount} photo(s) failed to upload. The invoice was saved without those photos.`);
    }
    // Delete removed existing photos
    if (isEdit && editInvoice?.photos) {
      try {
        const oldPhotos = typeof editInvoice.photos === 'string' ? JSON.parse(editInvoice.photos) : (editInvoice.photos || []);
        const currentExisting = photos.filter(p => p.existing).map(p => p.url);
        for (const url of oldPhotos) {
          if (!currentExisting.includes(url)) {
            try { await invoiceAPI.deletePhoto(editInvoice.id, url); } catch {}
          }
        }
      } catch {}
    }
  };

  // Serial number scan via live camera + OCR
  const [scanningKey, setScanningKey] = useState(null);
  const [scanningField, setScanningField] = useState('serial_number_1'); // 'serial_number_1' or 'serial_number_2'

  const scanSerialNumber = (itemKey, field = 'serial_number_1') => {
    setScanningKey(itemKey);
    setScanningField(field);
    setShowOCRScanner(true);
  };

  const handleOCRConfirm = (text) => {
    if (scanningKey && text) {
      updateItem(scanningKey, scanningField, text);
    }
    setShowOCRScanner(false);
    setScanningKey(null);
  };

  const handleOCRCancel = () => {
    setShowOCRScanner(false);
    setScanningKey(null);
  };

  const handleOCRAIScan = async (blob) => {
    const formData = new FormData();
    formData.append('file', blob, 'serial_scan.jpg');
    try {
      const res = await invoiceAPI.scanSerial(formData);
      return { serials: res.data.serials || [], raw_text: res.data.raw_text || '', error: res.data.error };
    } catch (err) {
      return { serials: [], raw_text: '', error: err.response?.data?.detail || 'Scan failed' };
    }
  };

  // Compute paid & balance from payment entries
  const totalPaid = paymentEntries.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const balanceAmount = grandTotal - totalPaid;

  // Helper to update a payment entry field
  const updatePaymentEntry = (idx, field, value) => {
    setPaymentEntries(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };
  const removePaymentEntry = (idx) => {
    setPaymentEntries(prev => prev.length <= 1 ? [{ mode: 'Cash', amount: '', date: new Date().toISOString().split('T')[0] }] : prev.filter((_, i) => i !== idx));
  };
  const addPaymentEntry = () => {
    const remaining = grandTotal - totalPaid;
    setPaymentEntries(prev => [...prev, { mode: 'Cash', amount: remaining > 0 ? remaining.toFixed(2) : '', date: new Date().toISOString().split('T')[0] }]);
  };

  const handleSubmit = async e => {
    e && e.preventDefault();
    if (cart.length === 0) { alert('Add at least one item'); return; }
    if (cart.some(i => !i.product_name.trim())) { alert('All items must have a name'); return; }
    if (!selectedCustomer && !walkinName.trim()) { alert('Please select a customer or enter a walk-in name'); return; }
    // Validate finance/cheque entries
    const hasFinance = paymentEntries.some(e => e.mode === 'Finance');
    const hasCheque = paymentEntries.some(e => e.mode === 'Cheque');
    if (hasFinance && !financeCompany.trim()) { alert('Please enter the finance company name'); return; }
    if (hasCheque && !chequeNumber.trim()) { alert('Please enter the cheque number'); return; }
    // If there's remaining balance, confirm credit
    if (balanceAmount > 0.01 && !showCreditConfirm) {
      setShowCreditConfirm(true);
      return;
    }
    setShowCreditConfirm(false);
    await doSubmit();
  };

  const doSubmit = async () => {
    setSaving(true);
    try {
      const custName = selectedCustomer ? selectedCustomer.name : (walkinName.trim() || 'Walk-in Customer');
      const custPhone = selectedCustomer ? (selectedCustomer.mobile || null) : (walkinPhone.trim() || null);
      const custAddr = selectedCustomer ? (selectedCustomer.address || null) : (walkinAddress.trim() || null);
      const finalDeliveryAddress = deliveryAddress.trim() || custAddr || null;
      const custGstin = selectedCustomer ? (selectedCustomer.gstin || null) : (walkinGstin.trim() || null);

      // Build final payment entries - filter out empty amounts
      const finalEntries = paymentEntries.filter(e => parseFloat(e.amount) > 0).map(e => ({ mode: e.mode, amount: parseFloat(e.amount), date: e.date || new Date().toISOString().split('T')[0] }));

      // Determine primary payment mode from entries
      const primaryMode = finalEntries.length > 0 ? finalEntries[0].mode : (totalPaid <= 0 ? 'Credit' : 'Cash');

      // For Finance, Credit, and Cheque payments, don't count as paid - keep as pending/outstanding
      const pendingModes = ['Finance', 'Credit', 'Cheque'];
      const confirmedPaid = finalEntries.filter(e => !pendingModes.includes(e.mode)).reduce((s, e) => s + e.amount, 0);
      const pendingAmount = finalEntries.filter(e => pendingModes.includes(e.mode)).reduce((s, e) => s + e.amount, 0);
      const effectiveBalance = grandTotal - confirmedPaid;
      const computedStatus = confirmedPaid >= grandTotal - 0.01 ? 'paid' : (confirmedPaid > 0 ? 'partial' : 'unpaid');
      const hasFinanceEntry = finalEntries.some(e => e.mode === 'Finance');
      const hasChequeEntry = finalEntries.some(e => e.mode === 'Cheque');

      const payload = {
        customer_id: selectedCustomer?.id || editInvoice?.customer_id || null,
        customer_name: custName,
        customer_phone: custPhone,
        customer_address: finalDeliveryAddress,
        customer_gstin: custGstin,
        customer_state: customerState || null,
        bill_type: billType,
        invoice_date: invoiceDate || new Date().toISOString().split('T')[0],
        items: cart.map(i => ({
          product_id: i.product_id,
          product_name: i.product_name,
          description: [i.description, (() => { const sn = [i.serial_number_1, i.serial_number_2].filter(Boolean).join(' / '); return sn ? `S/N: ${sn}` : ''; })()].filter(Boolean).join(' | ') || null,
          hsn_sac: i.hsn_sac || null,
          unit: i.unit || 'Pcs',
          quantity: Number.isFinite(parseFloat(i.quantity)) ? parseFloat(i.quantity) : 1,
          price: n(i.price),
          discount_percent: n(i.discount_percent),
          gst_percent: isGST ? n(i.gst_percent) : 0,
          gst_inclusive: !!i.gst_inclusive,
          total: n(i.total),
          cgst_rate: isGST ? n(i.gst_percent) / 2 : 0,
          sgst_rate: isGST ? n(i.gst_percent) / 2 : 0,
          igst_rate: 0,
          cess_rate: 0,
          is_igst: false,
          mrp: n(i.mrp) || null,
          warranty: i.warranty || null,
          warranty_type: i.warranty_type || null,
          warranty_expiry: i.warranty_expiry || null,
          expiry_date: i.expiry_date || null,
          cess_rate: n(i.cess_rate) || 0,
          alt_unit: i.alt_unit || null,
          alt_qty: n(i.alt_qty) || null,
        })),
        subtotal: parseFloat(taxableAmt.toFixed(2)),
        gst_total: parseFloat(gstTotal.toFixed(2)),
        grand_total: parseFloat(grandTotal.toFixed(2)),
        discount_amount: parseFloat(discountAmount) || 0,
        shipping_amount: parseFloat(shippingAmount) || 0,
        cgst_amount: parseFloat((gstTotal / 2).toFixed(2)),
        sgst_amount: parseFloat((gstTotal / 2).toFixed(2)),
        igst_amount: 0,
        cess_amount: 0,
        round_off: parseFloat(roundOff) || 0,
        sold_by: soldBy || null,
        doc_reference: docReference || null,
        doc_reference_no: docReferenceNo || null,
        tax_method: taxMethod || 'exclusive',
        shipping_tax_rate: parseFloat(shippingTaxRate) || 0,
        shipping_cess_rate: parseFloat(shippingCessRate) || 0,
        status: invoiceStatus || 'active',
        due_date: dueDate || null,
        tcs_rate: parseFloat(tcsRate) || 0,
        tcs_amount: parseFloat(tcsAmount) || 0,
        po_number: poNumber || null,
        po_date: poDate || null,
        delivery_terms: deliveryTerms || null,
        transport_mode: transportMode || null,
        vehicle_number: vehicleNumber || null,
        transporter_name: transporterName || null,
        transporter_id: transporterId || null,
        transport_distance: parseInt(transportDistance) || null,
        eway_bill_number: ewayBillNumber || null,
        payment_mode: primaryMode,
        payment_status: computedStatus,
        paid_amount: parseFloat(confirmedPaid.toFixed(2)),
        balance_amount: parseFloat(Math.max(0, effectiveBalance).toFixed(2)),
        notes: notes.trim() || null,
        payment_entries: finalEntries.length > 0 ? finalEntries : undefined,
        cheque_bank: hasChequeEntry ? chequeBank || undefined : undefined,
        cheque_number: hasChequeEntry ? chequeNumber || undefined : undefined,
        cheque_date: hasChequeEntry ? chequeDate || undefined : undefined,
        finance_company: hasFinanceEntry ? financeCompany || undefined : undefined,
        finance_amount: hasFinanceEntry ? parseFloat(financeAmount) || undefined : undefined,
        finance_scheme: hasFinanceEntry ? financeScheme || undefined : undefined,
        finance_product: hasFinanceEntry ? financeProduct || undefined : undefined,
        finance_emi_date: hasFinanceEntry ? financeEmiDate || undefined : undefined,
        finance_emi_amount: hasFinanceEntry ? parseFloat(financeEmiAmount) || undefined : undefined,
        finance_processing_fee: hasFinanceEntry ? parseFloat(financeProcessingFee) || undefined : undefined,
        finance_emi_tenure: hasFinanceEntry && financeEmiTenure ? parseInt(financeEmiTenure, 10) || undefined : undefined,
        finance_emi_end_date: hasFinanceEntry && financeEmiDate && financeEmiTenure ? (() => {
          const d = new Date(financeEmiDate);
          d.setMonth(d.getMonth() + parseInt(financeEmiTenure, 10));
          return d.toISOString().split('T')[0];
        })() : undefined,
      };

      if (isEdit) {
        await invoiceAPI.fullUpdate(editInvoice.id, payload);
        const origPhotoCount = (() => { try { const p = typeof editInvoice?.photos === 'string' ? JSON.parse(editInvoice.photos) : editInvoice?.photos; return Array.isArray(p) ? p.length : 0; } catch { return 0; } })();
        if (photos.some(p => !p.existing) || photos.length !== origPhotoCount) {
          await uploadPhotosForInvoice(editInvoice.id);
        }
        onSuccess(null);
      } else {
        const res = await invoiceAPI.create(payload);
        if (photos.length > 0) {
          await uploadPhotosForInvoice(res.data.id);
        }
        // Check for out-of-stock items
        if (res.data.out_of_stock_items && res.data.out_of_stock_items.length > 0) {
          setOosNotification({ invoiceId: res.data.id, items: res.data.out_of_stock_items });
        } else {
          onSuccess(res.data.id);
        }
        refreshProducts(true);
      }
    } catch (err) { alert(err.response?.data?.detail || (isEdit ? 'Failed to update invoice' : 'Failed to create invoice')); }
    setSaving(false);
  };

  const SectionTitle = ({ children }) => (
    <div style={{ ...pg, fontWeight: 700, fontSize: 14, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
  );

  const tblInp = (extra = {}) => ({ ...inp, padding: '5px 7px', border: 'none', background: 'transparent', width: '100%', ...extra });
  const tblCell = { padding: '6px 4px', verticalAlign: 'middle', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` };

  return (
    <>
    {/* Outer modal container — matches Purchase Bill style */}
    <div style={{ background: darkMode ? '#0F172A' : '#E2E8F0', borderRadius: 18, width: '100%', maxWidth: modalMaxWidth, maxHeight: '98dvh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', padding: mobile ? 8 : 12, boxSizing: 'border-box' }}>
    <div style={{ background: darkMode ? '#1E293B' : '#fff', borderRadius: 14, border: `2px solid ${darkMode ? '#334155' : '#CBD5E1'}` }}>
      {/* Top accent bar */}
      <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)`, borderRadius: '14px 14px 0 0' }} />
    <form onSubmit={handleSubmit}>
      {/* Sticky header — title + bill type tabs + close button */}
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, position: 'sticky', top: 0, background: darkMode ? '#1E293B' : '#fff', zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          <h3 style={{ color: darkMode ? '#F1F5F9' : '#0F172A', fontSize: 15, fontWeight: 800, margin: 0, whiteSpace: 'nowrap' }}>
            {isEdit ? '✏️ Edit Invoice' : '🧾 New Invoice'}
          </h3>
          <div style={{ display: 'flex', background: darkMode ? '#0F172A' : '#F1F5F9', borderRadius: 8, padding: 2, gap: 1, flexWrap: 'wrap' }}>
            {BILL_TYPES.map(bt => (
              <button key={bt.value} type="button" onClick={() => setBillType(bt.value)}
                style={{ padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 11,
                  background: billType === bt.value ? acc : 'transparent',
                  color: billType === bt.value ? '#fff' : (darkMode ? '#64748B' : '#94A3B8'), whiteSpace: 'nowrap' }}>
                {bt.icon} {bt.label}
              </button>
            ))}
          </div>
        </div>
        {onClose && (
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: darkMode ? '#64748B' : '#94A3B8', lineHeight: 1, padding: '2px 6px', flexShrink: 0 }}>×</button>
        )}
      </div>
      {/* Scrollable content */}
      <div style={{ padding: mobile ? '12px' : '16px' }}>

      {/* Row 1: Customer + Photos side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 320px', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>

          {/* Customer */}
          <div style={{ ...card, padding: 16, minWidth: 0 }}>
            <SectionTitle>👤 Customer Details</SectionTitle>
            {selectedCustomer ? (
              <div style={{ padding: '10px 14px', background: darkMode ? '#0F172A' : '#eff6ff', borderRadius: 8, border: `1px solid ${acc}40`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontWeight: 700, ...pg, fontSize: 14 }}>{selectedCustomer.name}</div>
                  {selectedCustomer.mobile && <div style={{ fontSize: 12, ...muted }}>📞 {selectedCustomer.mobile}</div>}
                  {selectedCustomer.gstin && <div style={{ fontSize: 11, ...muted }}>GSTIN: {selectedCustomer.gstin}</div>}
                  {selectedCustomer.address && <div style={{ fontSize: 12, ...muted }}>📍 {selectedCustomer.address}</div>}
                </div>
                <button type="button" onClick={() => setSelectedCustomer(null)} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ position: 'relative' }}>
                  <input style={inp} value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder="🔍 Search existing customer by name or phone…" />
                  {showCustDD && customers.length > 0 && (
                    <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 20, background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 200, overflowY: 'auto' }}>
                      {customers.map(c => (
                        <button key={c.id} type="button" onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setShowCustDD(false); }}
                          style={{ width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, ...pg }}>
                          <strong>{c.name}</strong>
                          {c.customer_type === 'business' && <span style={{ marginLeft: 6, fontSize: 10, color: '#3b82f6' }}>🏢</span>}
                          <span style={{ marginLeft: 8, fontSize: 11, ...muted }}>{[c.mobile, c.mobile2, c.mobile3].filter(Boolean).join(' / ') || 'No phone'}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Quick-add customer inline */}
                {!showQuickCust ? (
                  <button type="button" onClick={() => setShowQuickCust(true)}
                    style={{ alignSelf: 'flex-start', padding: '5px 14px', border: `1px dashed ${acc}`, borderRadius: 7, background: 'transparent', color: acc, cursor: 'pointer', fontSize: 11, fontWeight: 700 }}>
                    + New Customer
                  </button>
                ) : (
                  <div style={{ padding: 12, background: darkMode ? '#0F172A' : '#eff6ff', borderRadius: 8, border: `1px solid ${acc}40` }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: acc, marginBottom: 8 }}>Quick Add Customer</div>
                    <div style={{ display: 'grid', gridTemplateColumns: pairCols, gap: 8, marginBottom: 8 }}>
                      <input style={inp} value={quickCustName} onChange={e => setQuickCustName(e.target.value)} placeholder="Customer name *" />
                      <input style={inp} value={quickCustPhone} onChange={e => setQuickCustPhone(e.target.value)} placeholder="Phone number" />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <input style={inp} value={quickCustAddress} onChange={e => setQuickCustAddress(e.target.value)} placeholder="Address (Street, City, Pincode)" />
                    </div>
                    <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: 8 }}>
                      <button type="button" disabled={quickCustSaving || !quickCustName.trim()} onClick={async () => {
                        setQuickCustSaving(true);
                        try {
                          const res = await customerAPI.create({ name: quickCustName.trim(), mobile: quickCustPhone.trim() || null, address: quickCustAddress.trim() || null });
                          setSelectedCustomer(res.data);
                          setQuickCustName(''); setQuickCustPhone(''); setQuickCustAddress(''); setShowQuickCust(false);
                          refreshCustomers(true);
                        } catch (e) { alert('Failed to create customer: ' + (e.response?.data?.detail || e.message)); }
                        setQuickCustSaving(false);
                      }} style={{ padding: '6px 16px', background: acc, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: (!quickCustName.trim() || quickCustSaving) ? 0.5 : 1 }}>
                        {quickCustSaving ? 'Saving…' : 'Save & Select'}
                      </button>
                      <button type="button" onClick={() => { setShowQuickCust(false); setQuickCustName(''); setQuickCustPhone(''); setQuickCustAddress(''); }}
                        style={{ padding: '6px 12px', background: 'none', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 6, color: darkMode ? '#94A3B8' : '#64748B', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                    </div>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: pairCols, gap: 10 }}>
                  <div><label style={lbl}>Walk-in Name<span style={req}>*</span></label><input style={inp} value={walkinName} onChange={e => setWalkinName(e.target.value)} placeholder="Customer name" /></div>
                  <div><label style={lbl}>Phone</label><input style={inp} value={walkinPhone} onChange={e => setWalkinPhone(e.target.value)} placeholder="+91 99999 99999" /></div>
                </div>
                <div><label style={lbl}>Address (optional)</label><input style={inp} value={walkinAddress} onChange={e => setWalkinAddress(e.target.value)} placeholder="Street, City, Pincode" /></div>
                <div><label style={lbl}>GSTIN (optional)</label><input style={inp} value={walkinGstin} onChange={e => setWalkinGstin(e.target.value)} placeholder="27AABCU9603R1ZX" maxLength={15} /></div>
              </div>
            )}
          </div>

          {/* Photo Attachments (beside customer) */}
          <div style={{ ...card, padding: 16, alignSelf: 'flex-start' }}>
            <SectionTitle>📷 Photo Attachments (optional)</SectionTitle>
            <p style={{ ...muted, fontSize: 11, marginBottom: 8 }}>Max 5 photos · Tap to view · Will be printed below invoice</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
              {photos.map((ph, idx) => (
                <div key={idx} style={{ width: 64, height: 64, borderRadius: 6, overflow: 'hidden', position: 'relative', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, cursor: 'pointer' }}>
                  <img src={ph.url || ph} alt="" onClick={() => setPhotoLightbox(idx)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button type="button" onClick={(e) => { e.stopPropagation(); removePhoto(idx); }}
                    style={{ position: 'absolute', top: 1, right: 1, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', borderRadius: 4, width: 18, height: 18, fontSize: 12, cursor: 'pointer', lineHeight: '16px', padding: 0 }}>×</button>
                </div>
              ))}
            </div>
            {photos.length < 5 && (
              <div style={{ display: 'flex', gap: 6 }}>
                <label style={{ padding: '5px 12px', background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px dashed ${acc}`, borderRadius: 6, color: acc, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  📎 Upload
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
                    onChange={e => handlePhotoFiles(e.target.files)} />
                </label>
                <button type="button" onClick={openCamera}
                  style={{ padding: '5px 12px', background: darkMode ? '#0F172A' : '#F8FAFC', border: `1px dashed ${acc}`, borderRadius: 6, color: acc, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  📸 Camera
                </button>
              </div>
            )}
            {/* Hidden camera input */}
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden
              onChange={e => handlePhotoFiles(e.target.files)} />
          </div>
      </div>

      {/* Row 1.5: Invoice Details */}
      <div style={{ ...card, padding: 16, marginBottom: 16 }}>
        <SectionTitle>📋 Invoice Details</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 10 }}>
          <div><label style={lbl}>Invoice Date</label><StrictDateInput style={inp} value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} /></div>
          <div><label style={lbl}>Delivery Address</label><input style={inp} value={deliveryAddress} onChange={e => setDeliveryAddress(e.target.value)} placeholder="Street, City, Pincode" /></div>
          <div><label style={lbl}>Due Date</label><StrictDateInput style={inp} value={dueDate} onChange={e => setDueDate(e.target.value)} /></div>
          <div><label style={lbl}>Transporter Name</label><input style={inp} value={transporterName} onChange={e => setTransporterName(e.target.value)} placeholder="Carrier name" /></div>
        </div>
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={() => setShowMoreInvoiceDetails(v => !v)}
            style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${acc}55`, background: 'transparent', color: acc, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}
          >
            {showMoreInvoiceDetails ? 'Show Less' : 'Show More'}
          </button>
        </div>
        {showMoreInvoiceDetails && (
          <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 10 }}>
          <div><label style={lbl}>Customer State</label><input style={inp} value={customerState} onChange={e => setCustomerState(e.target.value)} placeholder="State / UT" /></div>
          <div><label style={lbl}>Tax Method</label>
            <select style={inp} value={taxMethod} onChange={e => setTaxMethod(e.target.value)}>
              <option value="exclusive">Exclusive</option><option value="inclusive">Inclusive</option>
            </select>
          </div>
          <div><label style={lbl}>Sold By</label><input style={inp} value={soldBy} onChange={e => setSoldBy(e.target.value)} placeholder="Salesperson" /></div>
          <div><label style={lbl}>Invoice Status</label>
            <select style={inp} value={invoiceStatus} onChange={e => setInvoiceStatus(e.target.value)}>
              {['active','draft','cancelled','returned'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
          </div>
          <div><label style={lbl}>PO Number</label><input style={inp} value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="PO #" /></div>
          <div><label style={lbl}>PO Date</label><StrictDateInput style={inp} value={poDate} onChange={e => setPoDate(e.target.value)} /></div>
          <div><label style={lbl}>Doc Reference</label><input style={inp} value={docReference} onChange={e => setDocReference(e.target.value)} placeholder="DC / Challan" /></div>
          <div><label style={lbl}>Doc Reference No.</label><input style={inp} value={docReferenceNo} onChange={e => setDocReferenceNo(e.target.value)} /></div>
          <div><label style={lbl}>Delivery Terms</label><input style={inp} value={deliveryTerms} onChange={e => setDeliveryTerms(e.target.value)} placeholder="FOB, CIF, etc." /></div>
          <div><label style={lbl}>Transport Mode</label>
            <select style={inp} value={transportMode} onChange={e => setTransportMode(e.target.value)}>
              <option value="">-- Select --</option><option value="Road">Road</option><option value="Rail">Rail</option><option value="Air">Air</option><option value="Ship">Ship</option>
            </select>
          </div>
          <div><label style={lbl}>Vehicle No.</label><input style={inp} value={vehicleNumber} onChange={e => setVehicleNumber(e.target.value)} placeholder="AP 00 XX 0000" /></div>
          <div><label style={lbl}>Transporter ID</label><input style={inp} value={transporterId} onChange={e => setTransporterId(e.target.value)} placeholder="GSTIN of transporter" /></div>
          <div><label style={lbl}>Distance (km)</label><input style={inp} type="number" min="0" value={transportDistance} onChange={e => setTransportDistance(e.target.value)} placeholder="0" /></div>
          <div><label style={lbl}>E-Way Bill No.</label><input style={inp} value={ewayBillNumber} onChange={e => setEwayBillNumber(e.target.value)} placeholder="E-Way bill number" /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginTop: 10, padding: '10px 0', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
          <div><label style={lbl}>Discount (₹)</label><input style={inp} type="number" min="0" step="0.01" value={discountAmount} onChange={e => setDiscountAmount(e.target.value)} placeholder="0.00" /></div>
          <div><label style={lbl}>Shipping (₹)</label><input style={inp} type="number" min="0" step="0.01" value={shippingAmount} onChange={e => setShippingAmount(e.target.value)} placeholder="0.00" /></div>
          <div><label style={lbl}>Shipping Tax %</label><input style={inp} type="number" min="0" step="0.01" value={shippingTaxRate} onChange={e => setShippingTaxRate(e.target.value)} placeholder="0" /></div>
          <div><label style={lbl}>Shipping Cess %</label><input style={inp} type="number" min="0" step="0.01" value={shippingCessRate} onChange={e => setShippingCessRate(e.target.value)} placeholder="0" /></div>
          <div><label style={lbl}>Round Off (₹)</label><input style={inp} type="number" step="0.01" value={roundOff} onChange={e => setRoundOff(e.target.value)} placeholder="0.00" /></div>
          <div><label style={lbl}>TCS Rate %</label><input style={inp} type="number" min="0" step="0.01" value={tcsRate} onChange={e => setTcsRate(e.target.value)} placeholder="0" /></div>
          <div><label style={lbl}>TCS Amount (₹)</label><input style={inp} type="number" min="0" step="0.01" value={tcsAmount} onChange={e => setTcsAmount(e.target.value)} placeholder="0.00" /></div>
        </div>
          </>
        )}
      </div>

      {/* Row 2: Items - full width */}
      <div style={{ marginBottom: 16 }}>
          <div style={{ ...card, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <SectionTitle>📦 Items</SectionTitle>
            </div>

            {/* Product Search */}
            <div ref={prodRef} style={{ position: 'relative', marginBottom: 12 }}>
              <input style={{ ...inp, paddingLeft: 36 }} value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder="Search products by name or SKU to add…" />
              <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', ...muted, fontSize: 14 }}>🔍</span>
              {showProdDD && products.length > 0 && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 20, background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', maxHeight: 240, overflowY: 'auto' }}>
                  {products.map(p => (
                    <button key={p.id} type="button" onClick={() => addToCart(p)}
                      style={{ width: '100%', textAlign: 'left', padding: '9px 12px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', ...pg }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                        <div style={{ fontSize: 10, ...muted }}>
                          SKU: {p.sku}{p.hsn_sac ? ` · HSN: ${p.hsn_sac}` : ''}{p.unit ? ` · ${p.unit}` : ''} · GST: {p.gst_percent}%
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
                        <div style={{ fontWeight: 700, color: '#16a34a', fontSize: 13 }}>₹{p.selling_price}</div>
                        <div style={{ fontSize: 10, color: p.current_stock <= 0 ? '#dc2626' : p.current_stock <= p.low_stock_threshold ? '#d97706' : '#6b7280' }}>
                          {p.current_stock <= 0 ? '❌ Out' : p.current_stock <= p.low_stock_threshold ? `⚠️ ${p.current_stock}` : `✓ ${p.current_stock}`} {p.unit}
                          {parseFloat(p.purchase_price) > 0 ? ` · Avg: ₹${parseFloat(p.purchase_price).toFixed(2)}` : ''}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Cart Table */}
            {cart.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '28px 0', ...muted, fontSize: 13, border: `1.5px dashed ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 8 }}>
                Search for a product above to begin adding items
              </div>
            ) : mobile ? (
              /* ── Mobile Card Layout ── */
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {cart.map((item, idx) => (
                  <div key={item.key} style={{ ...card, padding: 12, position: 'relative' }}>
                    <button type="button" onClick={() => removeItem(item.key)}
                      style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', color: '#dc2626', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '2px 4px', fontWeight: 700 }}>×</button>
                    <div style={{ fontSize: 10, ...muted, marginBottom: 4 }}>#{idx + 1}</div>
                    <input value={item.product_name} onChange={e => updateItem(item.key, 'product_name', e.target.value)}
                      placeholder="Item name" style={{ ...inp, fontWeight: 600, marginBottom: 6, padding: '6px 8px', fontSize: 13 }} />
                    <input value={item.description} onChange={e => updateItem(item.key, 'description', e.target.value)}
                      placeholder="Description (optional)" style={{ ...inp, fontSize: 11, marginBottom: 6, padding: '5px 8px', color: darkMode ? '#64748B' : '#94A3B8' }} />
                    {/* Serial Numbers */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 6, alignItems: 'center' }}>
                      <input value={item.serial_number_1 || ''} onChange={e => updateItem(item.key, 'serial_number_1', e.target.value)}
                        placeholder="SN 1" style={{ ...inp, fontSize: 10, fontFamily: 'monospace', color: '#7c3aed', flex: 1, padding: '5px 8px' }} />
                      <button type="button" onClick={() => scanSerialNumber(item.key, 'serial_number_1')} disabled={showOCRScanner}
                        style={{ padding: '4px 8px', background: 'none', border: `1px solid ${acc}`, borderRadius: 4, color: acc, fontSize: 9, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600, opacity: showOCRScanner ? 0.5 : 1 }}>
                        {scanningKey === item.key && scanningField === 'serial_number_1' ? '⏳' : '📷'}
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8, alignItems: 'center' }}>
                      <input value={item.serial_number_2 || ''} onChange={e => updateItem(item.key, 'serial_number_2', e.target.value)}
                        placeholder="SN 2 (optional)" style={{ ...inp, fontSize: 10, fontFamily: 'monospace', color: '#7c3aed', flex: 1, padding: '5px 8px' }} />
                      <button type="button" onClick={() => scanSerialNumber(item.key, 'serial_number_2')} disabled={showOCRScanner}
                        style={{ padding: '4px 8px', background: 'none', border: `1px solid ${acc}`, borderRadius: 4, color: acc, fontSize: 9, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600, opacity: showOCRScanner ? 0.5 : 1 }}>
                        {scanningKey === item.key && scanningField === 'serial_number_2' ? '⏳' : '📷'}
                      </button>
                    </div>
                    {/* Numeric fields in grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 6 }}>
                      <div>
                        <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>HSN</div>
                        <input value={item.hsn_sac} onChange={e => updateItem(item.key, 'hsn_sac', e.target.value)}
                          style={{ ...inp, padding: '5px 6px', fontSize: 11, fontFamily: 'monospace', textAlign: 'center' }} />
                      </div>
                      <div>
                        <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Unit</div>
                        <select value={item.unit} onChange={e => updateItem(item.key, 'unit', e.target.value)}
                          style={{ ...inp, padding: '5px 4px', fontSize: 11 }}>
                          {UNITS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </div>
                      <div>
                        <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Qty</div>
                        <input type="number" min="0.01" step="0.01" value={item.quantity} onChange={e => updateItem(item.key, 'quantity', e.target.value)}
                          style={{ ...inp, padding: '5px 6px', fontSize: 11, textAlign: 'right' }} />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                      <div>
                        <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>MRP (₹)</div>
                        <input type="number" min="0" step="0.01" value={item.mrp || ''} onChange={e => updateItem(item.key, 'mrp', e.target.value)}
                          style={{ ...inp, padding: '5px 6px', fontSize: 11, textAlign: 'right' }} />
                      </div>
                      <div>
                        <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Rate (₹)</div>
                        <input type="number" min="0" step="0.01" value={item.price} onChange={e => updateItem(item.key, 'price', e.target.value)}
                          style={{ ...inp, padding: '5px 6px', fontSize: 11, textAlign: 'right' }} />
                      </div>
                      <div>
                        <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Disc%</div>
                        <input type="number" min="0" max="100" step="0.5" value={item.discount_percent} onChange={e => updateItem(item.key, 'discount_percent', e.target.value)}
                          style={{ ...inp, padding: '5px 6px', fontSize: 11, textAlign: 'right', color: n(item.discount_percent) > 0 ? '#d97706' : undefined }} />
                      </div>
                      {isGST && (
                        <div>
                          <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>GST%</div>
                          <select value={gstSelectVal(item)} onChange={e => handleGSTSelect(item.key, e.target.value)}
                            style={{ ...inp, padding: '5px 4px', fontSize: 11 }}>
                            {GST_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                    {/* Totals row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, paddingTop: 8 }}>
                      <span style={{ ...muted, fontSize: 11 }}>Taxable: ₹{fmtNum(n(item._taxable))}</span>
                      <span style={{ fontWeight: 700, color: '#16a34a', fontSize: 14 }}>₹{fmtNum(n(item.total))}</span>
                    </div>
                    {/* Expandable extra fields */}
                    <details style={{ marginTop: 8 }}>
                      <summary style={{ ...muted, fontSize: 10, cursor: 'pointer', userSelect: 'none' }}>More Details (Warranty, Expiry, Alt Unit...)</summary>
                      <div style={{ display: 'grid', gridTemplateColumns: pairCols, gap: 6, marginTop: 8 }}>
                        <div>
                          <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Warranty</div>
                          <input value={item.warranty || ''} onChange={e => updateItem(item.key, 'warranty', e.target.value)}
                            placeholder="e.g. 1 Year" style={{ ...inp, padding: '5px 6px', fontSize: 11 }} />
                        </div>
                        <div>
                          <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Warranty Type</div>
                          <select value={item.warranty_type || ''} onChange={e => updateItem(item.key, 'warranty_type', e.target.value)}
                            style={{ ...inp, padding: '5px 4px', fontSize: 11 }}>
                            <option value="">--</option><option value="Manufacturer">Manufacturer</option><option value="Seller">Seller</option><option value="Extended">Extended</option>
                          </select>
                        </div>
                        <div>
                          <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Warranty Expiry</div>
                          <StrictDateInput value={item.warranty_expiry || ''} onChange={e => updateItem(item.key, 'warranty_expiry', e.target.value)}
                            style={{ ...inp, padding: '5px 6px', fontSize: 11 }} />
                        </div>
                        <div>
                          <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Expiry Date</div>
                          <StrictDateInput value={item.expiry_date || ''} onChange={e => updateItem(item.key, 'expiry_date', e.target.value)}
                            style={{ ...inp, padding: '5px 6px', fontSize: 11 }} />
                        </div>
                        <div>
                          <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Cess %</div>
                          <input type="number" min="0" step="0.01" value={item.cess_rate || ''} onChange={e => updateItem(item.key, 'cess_rate', e.target.value)}
                            style={{ ...inp, padding: '5px 6px', fontSize: 11, textAlign: 'right' }} />
                        </div>
                        <div>
                          <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Alt Unit</div>
                          <input value={item.alt_unit || ''} onChange={e => updateItem(item.key, 'alt_unit', e.target.value)}
                            placeholder="e.g. Box" style={{ ...inp, padding: '5px 6px', fontSize: 11 }} />
                        </div>
                        <div>
                          <div style={{ ...lbl, fontSize: 9, marginBottom: 2 }}>Alt Qty</div>
                          <input type="number" min="0" step="0.01" value={item.alt_qty || ''} onChange={e => updateItem(item.key, 'alt_qty', e.target.value)}
                            style={{ ...inp, padding: '5px 6px', fontSize: 11, textAlign: 'right' }} />
                        </div>
                      </div>
                    </details>
                  </div>
                ))}
              </div>
            ) : (
              /* ── Desktop Table Layout ── */
              <div style={{ overflowX: 'auto', borderRadius: 8, border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 680 }}>
                  <thead>
                    <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC', borderBottom: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}` }}>
                      {['#', 'Item Name / Description', 'HSN', 'Unit', 'Qty', 'MRP', 'Rate (₹)', 'Disc%', isGST ? 'GST%' : null, 'Taxable', 'Total', ''].filter(Boolean).map(h => (
                        <th key={h} style={{ padding: '8px 6px', textAlign: h === 'Item Name / Description' ? 'left' : 'right', fontSize: 10, fontWeight: 700, ...muted, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cart.map((item, idx) => (
                      <tr key={item.key} style={{ borderBottom: `1px solid ${darkMode ? '#1E293B' : '#F1F5F9'}` }}>
                        {/* # */}
                        <td style={{ ...tblCell, paddingLeft: 8, color: darkMode ? '#64748B' : '#94A3B8', fontSize: 11, width: 24, textAlign: 'right' }}>{idx + 1}</td>

                        {/* Item Name + Description + Serial Numbers */}
                        <td style={{ ...tblCell, minWidth: 160 }}>
                          <input value={item.product_name} onChange={e => updateItem(item.key, 'product_name', e.target.value)}
                            placeholder="Item name" style={{ ...tblInp(), fontWeight: 600, ...pg, marginBottom: 2 }} />
                          <input value={item.description} onChange={e => updateItem(item.key, 'description', e.target.value)}
                            placeholder="Description (optional)" style={{ ...tblInp(), fontSize: 10, color: darkMode ? '#64748B' : '#94A3B8' }} />
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                            <input value={item.serial_number_1 || ''} onChange={e => updateItem(item.key, 'serial_number_1', e.target.value)}
                              placeholder="SN 1" style={{ ...tblInp(), fontSize: 10, fontFamily: 'monospace', color: '#7c3aed', flex: 1 }} />
                            <button type="button" onClick={() => scanSerialNumber(item.key, 'serial_number_1')}
                              disabled={showOCRScanner}
                              style={{ padding: '2px 5px', background: 'none', border: `1px solid ${acc}`, borderRadius: 4, color: acc, fontSize: 8, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600, opacity: showOCRScanner ? 0.5 : 1 }}>
                              {scanningKey === item.key && scanningField === 'serial_number_1' ? '⏳' : '📷1'}
                            </button>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                            <input value={item.serial_number_2 || ''} onChange={e => updateItem(item.key, 'serial_number_2', e.target.value)}
                              placeholder="SN 2 (opt)" style={{ ...tblInp(), fontSize: 10, fontFamily: 'monospace', color: '#7c3aed', flex: 1 }} />
                            <button type="button" onClick={() => scanSerialNumber(item.key, 'serial_number_2')}
                              disabled={showOCRScanner}
                              style={{ padding: '2px 5px', background: 'none', border: `1px solid ${acc}`, borderRadius: 4, color: acc, fontSize: 8, cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: 600, opacity: showOCRScanner ? 0.5 : 1 }}>
                              {scanningKey === item.key && scanningField === 'serial_number_2' ? '⏳' : '📷2'}
                            </button>
                          </div>
                          {/* Extra item fields */}
                          <details style={{ marginTop: 4 }}>
                            <summary style={{ ...muted, fontSize: 9, cursor: 'pointer', userSelect: 'none' }}>More (Warranty, Expiry...)</summary>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginTop: 4 }}>
                              <input value={item.warranty || ''} onChange={e => updateItem(item.key, 'warranty', e.target.value)}
                                placeholder="Warranty" style={{ ...tblInp(), fontSize: 9 }} />
                              <select value={item.warranty_type || ''} onChange={e => updateItem(item.key, 'warranty_type', e.target.value)}
                                style={{ ...inp, padding: '2px 4px', fontSize: 9, width: '100%' }}>
                                <option value="">Type</option><option value="Manufacturer">Manufacturer</option><option value="Seller">Seller</option><option value="Extended">Extended</option>
                              </select>
                              <StrictDateInput value={item.warranty_expiry || ''} onChange={e => updateItem(item.key, 'warranty_expiry', e.target.value)}
                                title="Warranty Expiry" style={{ ...tblInp(), fontSize: 9 }} />
                              <StrictDateInput value={item.expiry_date || ''} onChange={e => updateItem(item.key, 'expiry_date', e.target.value)}
                                title="Expiry Date" style={{ ...tblInp(), fontSize: 9 }} placeholder="Expiry" />
                              <input type="number" min="0" step="0.01" value={item.cess_rate || ''} onChange={e => updateItem(item.key, 'cess_rate', e.target.value)}
                                placeholder="Cess%" style={{ ...tblInp({ textAlign: 'right' }), fontSize: 9 }} />
                              <div style={{ display: 'flex', gap: 2 }}>
                                <input value={item.alt_unit || ''} onChange={e => updateItem(item.key, 'alt_unit', e.target.value)}
                                  placeholder="Alt Unit" style={{ ...tblInp(), fontSize: 9, flex: 1 }} />
                                <input type="number" min="0" step="0.01" value={item.alt_qty || ''} onChange={e => updateItem(item.key, 'alt_qty', e.target.value)}
                                  placeholder="Alt Qty" style={{ ...tblInp({ textAlign: 'right' }), fontSize: 9, flex: 1 }} />
                              </div>
                            </div>
                          </details>
                        </td>

                        {/* HSN */}
                        <td style={{ ...tblCell, width: 72 }}>
                          <input value={item.hsn_sac} onChange={e => updateItem(item.key, 'hsn_sac', e.target.value)}
                            placeholder="HSN" style={{ ...tblInp(), fontFamily: 'monospace', fontSize: 11, textAlign: 'center' }} />
                        </td>

                        {/* Unit */}
                        <td style={{ ...tblCell, width: 62 }}>
                          <select value={item.unit} onChange={e => updateItem(item.key, 'unit', e.target.value)}
                            style={{ ...inp, padding: '4px 4px', fontSize: 11, width: '100%' }}>
                            {UNITS.map(u => <option key={u}>{u}</option>)}
                          </select>
                        </td>

                        {/* Qty */}
                        <td style={{ ...tblCell, width: 52 }}>
                          <input type="number" min="0.01" step="0.01" value={item.quantity} onChange={e => updateItem(item.key, 'quantity', e.target.value)}
                            style={{ ...tblInp({ textAlign: 'right', width: 46 }) }} />
                        </td>

                        {/* MRP */}
                        <td style={{ ...tblCell, width: 70 }}>
                          <input type="number" min="0" step="0.01" value={item.mrp || ''} onChange={e => updateItem(item.key, 'mrp', e.target.value)}
                            placeholder="MRP" style={{ ...tblInp({ textAlign: 'right' }) }} />
                        </td>

                        {/* Rate */}
                        <td style={{ ...tblCell, width: 80 }}>
                          <input type="number" min="0" step="0.01" value={item.price} onChange={e => updateItem(item.key, 'price', e.target.value)}
                            style={{ ...tblInp({ textAlign: 'right' }) }} />
                        </td>

                        {/* Disc% */}
                        <td style={{ ...tblCell, width: 54 }}>
                          <input type="number" min="0" max="100" step="0.5" value={item.discount_percent} onChange={e => updateItem(item.key, 'discount_percent', e.target.value)}
                            style={{ ...tblInp({ textAlign: 'right', color: n(item.discount_percent) > 0 ? '#d97706' : undefined }) }} />
                        </td>

                        {/* GST% */}
                        {isGST && (
                          <td style={{ ...tblCell, width: 58 }}>
                            <select value={gstSelectVal(item)} onChange={e => handleGSTSelect(item.key, e.target.value)}
                              style={{ ...inp, padding: '4px 4px', fontSize: 11, width: '100%' }}>
                              {GST_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                        )}

                        {/* Taxable */}
                        <td style={{ ...tblCell, width: 80, textAlign: 'right', ...muted, fontSize: 11 }}>
                          ₹{fmtNum(n(item._taxable))}
                        </td>

                        {/* Total */}
                        <td style={{ ...tblCell, width: 84, textAlign: 'right', fontWeight: 700, color: '#16a34a', paddingRight: 6 }}>
                          ₹{fmtNum(n(item.total))}
                        </td>

                        {/* Remove */}
                        <td style={{ ...tblCell, width: 28, paddingRight: 6, textAlign: 'center' }}>
                          <button type="button" onClick={() => removeItem(item.key)}
                            style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: '2px 4px' }}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
      </div>

      {/* Row 3: Payment + Summary side by side */}
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 320px', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>

          {/* Payment */}
          <div style={{ ...card, padding: 16 }}>
            <SectionTitle>💳 Payment</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {/* Payment entry rows */}
              {paymentEntries.map((pe, idx) => (
                <div key={idx} style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: 8, alignItems: mobile ? 'stretch' : 'flex-start' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr 1fr' : '120px 130px minmax(0, 1fr)', gap: 8, flex: 1, minWidth: 0 }}>
                    <select style={{ ...inp, width: '100%' }} value={pe.mode}
                      onChange={e => updatePaymentEntry(idx, 'mode', e.target.value)}>
                      {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
                    </select>
                    <StrictDateInput style={{ ...inp, width: '100%' }}
                      value={pe.date || ''}
                      onChange={e => updatePaymentEntry(idx, 'date', e.target.value)} />
                    <div style={{ position: 'relative', gridColumn: mobile ? '1 / -1' : 'auto' }}>
                      <input type="number" min="0" step="0.01" style={{ ...inp, fontWeight: 600 }}
                        value={pe.amount}
                        onChange={e => updatePaymentEntry(idx, 'amount', e.target.value)}
                        placeholder={`₹ ${idx === 0 ? fmtNum(grandTotal) : '0.00'}`}
                      />
                    </div>
                  </div>
                  <button type="button" onClick={() => removePaymentEntry(idx)}
                    style={{ alignSelf: mobile ? 'flex-end' : 'center', background: 'none', border: 'none', color: '#dc2626', fontSize: 18, cursor: 'pointer', padding: '6px 4px', lineHeight: 1 }}>×</button>
                </div>
              ))}
              {/* Add Payment row button */}
              <button type="button" onClick={addPaymentEntry}
                style={{ padding: '7px 0', border: `1px dashed ${acc}`, borderRadius: 7, background: 'transparent', color: acc, fontSize: 12, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
                + Add Payment
              </button>
              {/* Balance summary */}
              {grandTotal > 0 && (() => {
                const pendingModeAmount = paymentEntries.filter(e => ['Finance', 'Credit', 'Cheque'].includes(e.mode)).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
                const confirmedAmount = totalPaid - pendingModeAmount;
                const isPending = balanceAmount <= 0.01 && pendingModeAmount > 0.01;
                const isPartialPending = balanceAmount <= 0.01 && pendingModeAmount > 0.01 && confirmedAmount > 0;
                const bgColor = balanceAmount > 0.01 ? (darkMode ? '#451a03' : '#FEF3C7') : isPending ? (darkMode ? '#1e3a5f' : '#EFF6FF') : (darkMode ? '#052e16' : '#DCFCE7');
                const statusColor = balanceAmount > 0.01 ? '#DC2626' : isPending ? '#2563EB' : '#16A34A';
                const statusText = balanceAmount > 0.01 ? `Balance: ₹${fmtNum(balanceAmount)}` : isPending ? `⏳ Pending: ₹${fmtNum(pendingModeAmount)}` : '✓ Fully Paid';
                return (
                  <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', justifyContent: 'space-between', gap: 6, padding: '8px 10px', background: bgColor, borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                    <span>Paid: ₹{fmtNum(confirmedAmount > 0 ? confirmedAmount : totalPaid)}</span>
                    <span style={{ color: statusColor }}>{statusText}</span>
                  </div>
                );
              })()}
              {/* Cheque details - shown if any entry uses Cheque */}
              {paymentEntries.some(e => e.mode === 'Cheque') && (
                <div style={{ padding: 10, background: darkMode ? '#0F172A' : '#FFF7ED', borderRadius: 8, border: `1px solid ${darkMode ? '#78350F' : '#FED7AA'}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#D97706' }}>🏦 Cheque Details</div>
                  <input style={inp} value={chequeBank} onChange={e => setChequeBank(e.target.value)} placeholder="Issuing Bank Name" />
                  <input style={inp} value={chequeNumber} onChange={e => setChequeNumber(e.target.value)} placeholder="Cheque Number" />
                  <div>
                    <label style={lbl}>Cheque Date</label>
                    <StrictDateInput style={inp} value={chequeDate} onChange={e => setChequeDate(e.target.value)} />
                  </div>
                </div>
              )}
              {/* Finance details - shown if any entry uses Finance */}
              {paymentEntries.some(e => e.mode === 'Finance') && (() => {
                // Calculate EMI end date from start date + tenure
                let emiEndDate = '';
                if (financeEmiDate && financeEmiTenure) {
                  const start = new Date(financeEmiDate);
                  start.setMonth(start.getMonth() + parseInt(financeEmiTenure, 10));
                  emiEndDate = start.toISOString().split('T')[0];
                }
                return (
                  <div style={{ padding: 10, background: darkMode ? '#0F172A' : '#EFF6FF', borderRadius: 8, border: `1px solid ${darkMode ? '#1E3A5F' : '#BFDBFE'}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#2563EB' }}>🏢 Finance Details</div>
                    <input style={inp} value={financeCompany} onChange={e => setFinanceCompany(e.target.value)} placeholder="Financing Company Name *" />
                    <div style={{ display: 'grid', gridTemplateColumns: pairCols, gap: 8 }}>
                      <div>
                        <label style={lbl}>Finance Amount</label>
                        <input type="number" min="0" step="0.01" style={inp} value={financeAmount} onChange={e => setFinanceAmount(e.target.value)} placeholder="₹ Amount" />
                      </div>
                      <div>
                        <label style={lbl}>Processing Fee</label>
                        <input type="number" min="0" step="0.01" style={inp} value={financeProcessingFee} onChange={e => setFinanceProcessingFee(e.target.value)} placeholder="₹ Fee" />
                      </div>
                    </div>
                    <input style={inp} value={financeScheme} onChange={e => setFinanceScheme(e.target.value)} placeholder="Finance Scheme (e.g. No-Cost EMI)" />
                    <input style={inp} value={financeProduct} onChange={e => setFinanceProduct(e.target.value)} placeholder="Product Name" />
                    <div style={{ display: 'grid', gridTemplateColumns: pairCols, gap: 8 }}>
                      <div>
                        <label style={lbl}>Start EMI Date</label>
                        <StrictDateInput style={inp} value={financeEmiDate} onChange={e => setFinanceEmiDate(e.target.value)} />
                      </div>
                      <div>
                        <label style={lbl}>EMI Tenure (months)</label>
                        <input type="number" min="1" max="120" step="1" style={inp} value={financeEmiTenure} onChange={e => setFinanceEmiTenure(e.target.value)} placeholder="e.g. 12" />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: pairCols, gap: 8 }}>
                      <div>
                        <label style={lbl}>EMI Amount (₹/month)</label>
                        <input type="number" min="0" step="0.01" style={inp} value={financeEmiAmount} onChange={e => setFinanceEmiAmount(e.target.value)} placeholder="₹/month" />
                      </div>
                      <div>
                        <label style={lbl}>EMI End Date</label>
                        <StrictDateInput  style={{ ...inp, background: darkMode ? '#1e293b' : '#f1f5f9', color: darkMode ? '#94A3B8' : '#64748B' }} value={emiEndDate} readOnly />
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div>
                <label style={lbl}>Notes (optional)</label>
                <textarea style={{ ...inp, minHeight: 54, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Remarks, reference, delivery info…" />
              </div>
            </div>
          </div>
        </div>

          {/* Live OCR Scanner Modal */}
          {showOCRScanner && (
            <LiveOCRScanner
              onConfirm={handleOCRConfirm}
              onCancel={handleOCRCancel}
              onAIScan={handleOCRAIScan}
              darkMode={darkMode}
              halfScreen={window.innerWidth < 1024}
            />
          )}

          {/* Summary (right side of Row 3) */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ ...card, padding: 16, ...(mobile ? {} : { position: 'sticky', top: 56 }) }}>
            <SectionTitle>🧮 Summary</SectionTitle>
            <div style={{ fontSize: 13 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span style={muted}>Gross Amount</span>
                <span style={pg}>₹{fmtNum(grossTotal)}</span>
              </div>
              {totalDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                  <span style={{ color: '#d97706' }}>Less: Discount</span>
                  <span style={{ color: '#d97706', fontWeight: 600 }}>-₹{fmtNum(totalDiscount)}</span>
                </div>
              )}
              {(totalDiscount > 0 || isGST) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderTop: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, marginTop: 2 }}>
                  <span style={muted}>Taxable Amount</span>
                  <span style={pg}>₹{fmtNum(taxableAmt)}</span>
                </div>
              )}
              {isGST && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={muted}>CGST</span>
                    <span style={pg}>₹{fmtNum(cgst)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                    <span style={muted}>SGST</span>
                    <span style={pg}>₹{fmtNum(sgst)}</span>
                  </div>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0 4px', borderTop: `2px solid ${darkMode ? '#475569' : '#CBD5E1'}`, marginTop: 4, fontWeight: 800, fontSize: 16 }}>
                <span style={pg}>Grand Total</span>
                <span style={{ color: '#16a34a' }}>₹{fmtNum(grandTotal)}</span>
              </div>
              {/* Paid & Balance display */}
              {grandTotal > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                    <span style={muted}>Paid Amount</span>
                    <span style={{ color: '#2563EB', fontWeight: 700 }}>₹{fmtNum(totalPaid)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 14, fontWeight: 800 }}>
                    <span style={{ color: balanceAmount > 0.01 ? '#DC2626' : '#16A34A' }}>Balance</span>
                    <span style={{ color: balanceAmount > 0.01 ? '#DC2626' : '#16A34A' }}>₹{fmtNum(Math.max(0, balanceAmount))}</span>
                  </div>
                  {balanceAmount > 0.01 && (
                    <div style={{ padding: '6px 10px', background: darkMode ? '#451a03' : '#FEF3C7', borderRadius: 6, border: `1px solid ${darkMode ? '#78350F' : '#FDE68A'}`, marginTop: 2 }}>
                      <span style={{ color: '#D97706', fontSize: 11, fontWeight: 600 }}>⚠️ Credit: ₹{fmtNum(balanceAmount)} will remain unpaid</span>
                    </div>
                  )}
                </>
              )}
              {cart.length > 0 && (
                <div style={{ fontSize: 11, ...muted, marginTop: 3 }}>{cart.length} item{cart.length > 1 ? 's' : ''}</div>
              )}
            </div>
            <button type="submit" disabled={saving || cart.length === 0}
              style={{ width: '100%', marginTop: 16, padding: '12px', background: saving || cart.length === 0 ? '#94a3b8' : acc, color: '#fff', border: 'none', borderRadius: 9, fontWeight: 800, fontSize: 14, cursor: saving || cart.length === 0 ? 'not-allowed' : 'pointer', transition: 'background 0.2s' }}>
              {saving ? '⏳ Saving…' : isEdit ? '💾 Update Invoice' : '🧾 Generate Bill & Print'}
            </button>
          </div>
        </div>
      </div>
      </div>  {/* end scrollable content */}
    </form>
    </div>  {/* end inner card */}
    </div>  {/* end outer modal */}

    {/* Credit Confirmation Dialog */}
    {showCreditConfirm && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={e => { if (e.target === e.currentTarget) setShowCreditConfirm(false); }}>
        <div style={{ ...card, padding: 28, maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', width: '100%', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ textAlign: 'center', fontSize: 44, marginBottom: 10 }}>💳</div>
          <h3 style={{ ...pg, textAlign: 'center', margin: '0 0 8px', fontSize: 17, fontWeight: 800 }}>Credit Sale Confirmation</h3>
          <p style={{ ...muted, textAlign: 'center', fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
            There is a remaining balance of
          </p>
          <div style={{ textAlign: 'center', fontSize: 28, fontWeight: 900, color: '#DC2626', marginBottom: 16 }}>
            ₹{fmtNum(balanceAmount)}
          </div>
          <p style={{ textAlign: 'center', fontSize: 13, color: darkMode ? '#94A3B8' : '#64748B', marginBottom: 20 }}>
            Do you really want to give products on credit?
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => { setShowCreditConfirm(false); }}
              style={{ flex: 1, padding: '11px 20px', background: darkMode ? '#334155' : '#E2E8F0', color: darkMode ? '#CBD5E1' : '#64748B', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              ✕ Cancel
            </button>
            <button type="button" onClick={async () => { setShowCreditConfirm(false); await doSubmit(); }}
              style={{ flex: 1, padding: '11px 20px', background: '#DC2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              ✓ Yes, Give on Credit
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Out-of-Stock Notification Modal */}
    {oosNotification && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={async (e) => { if (e.target !== e.currentTarget || oosLoading) return;
          setOosLoading(true);
          try { await Promise.all(oosNotification.items.map(item => item.notification_id ? paymentAPI.updateStockNotification(item.notification_id, 'dismissed') : Promise.resolve())); setOosNotification(null); onSuccess && onSuccess(oosNotification.invoiceId); } catch (err) { console.error(err); alert('Failed to update notifications'); }
          setOosLoading(false);
        }}>
        <div style={{ ...card, padding: 24, maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', width: '100%', borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
          <div style={{ textAlign: 'center', fontSize: 40, marginBottom: 10 }}>⚠️</div>
          <h3 style={{ ...pg, textAlign: 'center', margin: '0 0 6px', fontSize: 17, fontWeight: 800 }}>Items Out of Stock!</h3>
          <p style={{ ...muted, textAlign: 'center', fontSize: 12, marginBottom: 14 }}>
            Bill created successfully. The following items need to be ordered from suppliers:
          </p>
          <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 16 }}>
            {oosNotification.items.map((item, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: darkMode ? '#1E293B' : '#FEF3C7', borderRadius: 7, marginBottom: 4, fontSize: 13 }}>
                <span style={pg}>{item.product_name}</span>
                <span style={{ color: '#DC2626', fontWeight: 700 }}>Deficit: {item.deficit}</span>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', fontSize: 12, color: '#D97706', fontWeight: 600, marginBottom: 16 }}>
            📞 Call your supplier now and order these items!
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" disabled={oosLoading} onClick={async () => {
              setOosLoading(true);
              try {
                await Promise.all(oosNotification.items.map(item => item.notification_id ? paymentAPI.updateStockNotification(item.notification_id, 'ordered') : Promise.resolve()));
                setOosNotification(null);
                onSuccess && onSuccess(oosNotification.invoiceId);
              } catch (e) { console.error(e); alert('Failed to update notifications'); }
              setOosLoading(false);
            }}
              style={{ flex: 1, padding: '10px', background: '#16A34A', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: oosLoading ? 'not-allowed' : 'pointer', opacity: oosLoading ? 0.6 : 1 }}>
              {oosLoading ? '⏳ Updating…' : '✅ Order Placed Successfully'}
            </button>
            <button type="button" disabled={oosLoading} onClick={async () => {
              setOosLoading(true);
              try {
                await Promise.all(oosNotification.items.map(item => item.notification_id ? paymentAPI.updateStockNotification(item.notification_id, 'dismissed') : Promise.resolve()));
                setOosNotification(null);
                onSuccess && onSuccess(oosNotification.invoiceId);
              } catch (e) { console.error(e); alert('Failed to update notifications'); }
              setOosLoading(false);
            }}
              style={{ padding: '10px 20px', background: darkMode ? '#334155' : '#E2E8F0', color: darkMode ? '#CBD5E1' : '#64748B', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: oosLoading ? 'not-allowed' : 'pointer', opacity: oosLoading ? 0.6 : 1 }}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    {photoLightbox !== null && <PhotoLightbox photos={photos} initialIndex={photoLightbox} onClose={() => setPhotoLightbox(null)} />}
    </>
  );
};

export default Billing;
